// lib/permissions.ts
/**
 * This file defines the permissions system for the application
 * Extended with all database table permissions for complete RLS support
 * With DB support for dynamic permission loading
 */

// User role types
export type UserRole = 'admin' | 'subadmin' | 'bestatter' | 'branch' | 'user' | 'sds_user' | 'kreuzmal';
export const ALL_USER_ROLES = ['admin', 'subadmin', 'bestatter', 'branch', 'user', 'sds_user', 'kreuzmal'] as const;

// Permission types - VOLLSTÄNDIG erweitert mit allen DB-Table Permissions
export type Permission =
  // System
  | 'admin:dashboard:access'

    // Admin DashboardNavigation Permissions (NEU)
  | 'admin:nav:dashboard'
  | 'admin:nav:user_dashboard'
  | 'admin:nav:new_memorial'
  | 'admin:nav:all_memorials'
  | 'admin:nav:albums'
  | 'admin:nav:events'
  | 'admin:nav:family_trees'
  | 'admin:nav:collaborations'
  | 'admin:nav:memory_lights'
  | 'admin:nav:users'
  | 'admin:nav:permissions'
  | 'admin:nav:bestatter'
  | 'admin:nav:branches'
  | 'admin:nav:branches_structure'
  | 'admin:nav:embed_code'
  | 'admin:nav:sds_invoices'
  | 'admin:nav:sds_api_keys'
  | 'admin:nav:credits_packages'
  | 'admin:nav:credits_transfer'
  | 'admin:nav:credits_transactions'
  | 'admin:nav:quota_usage'
  | 'admin:nav:products'
  | 'admin:nav:bundles'
  | 'admin:nav:merchone'
  | 'admin:nav:merchone_catalog'
  | 'admin:nav:merchone_sync'
  | 'admin:nav:merchone_shop'
  | 'admin:nav:orders'
  | 'admin:nav:invoices'
  | 'admin:nav:plaques'
  | 'admin:nav:qrcodes'
  
  // Accounts
  | 'accounts:view:all'
  | 'accounts:create'
  | 'accounts:edit'
  | 'accounts:delete'
  
  // Activity Logs
  | 'activity_logs:view:all'
  | 'activity_logs:create'
  | 'activity_logs:edit'
  | 'activity_logs:delete'
  
  // API Keys & Request Logs
  | 'api_keys:view:all'
  | 'api_keys:create'
  | 'api_keys:edit'
  | 'api_keys:delete'
  | 'api_request_logs:view:all'
  | 'api_request_logs:create'
  | 'api_request_logs:edit'
  | 'api_request_logs:delete'
  | 'apikey:create'
  | 'apikey:edit'
  | 'apikey:delete'
  | 'apikey:view'
  | 'apikey:view:all'
  
  // Bestatter Branches
  | 'bestatter_branches:view:all'
  | 'bestatter_branches:create'
  | 'bestatter_branches:edit'
  | 'bestatter_branches:delete'
  
  // Bulk Operations
  | 'bulk_operations:view:all'
  | 'bulk_operations:create'
  | 'bulk_operations:edit'
  | 'bulk_operations:delete'
  
  // Cart Items
  | 'cart_items:view:all'
  | 'cart_items:create'
  | 'cart_items:edit'
  | 'cart_items:delete'
  
  // Collaboration & Invitations
  | 'collaboration:invite'
  | 'collaboration:manage'
  | 'collaboration:remove'
  | 'collaboration:view'
  | 'collaboration_invitations:view:all'
  | 'collaboration_invitations:create'
  | 'collaboration_invitations:edit'
  | 'collaboration_invitations:delete'
  | 'collaborator_activity_logs:view:all'
  | 'collaborator_activity_logs:create'
  | 'collaborator_activity_logs:edit'
  | 'collaborator_activity_logs:delete'
  | 'account:add_member'
  | 'account:remove_member'
  | 'account:manage_permissions'
  
  // Comments
  | 'comments:view:all'
  | 'comments:create'
  | 'comments:edit'
  | 'comments:delete'
  
  // Credits & Packages & Transactions
  | 'credit:read'
  | 'credit:purchase'
  | 'credits:view'
  | 'credits:edit'
  | 'credits:delete'
  | 'credits:manage'
  | 'credit_packages:view:all'
  | 'credit_packages:create'
  | 'credit_packages:edit'
  | 'credit_packages:delete'
  | 'credit_transactions:view:all'
  | 'credit_transactions:create'
  | 'credit_transactions:edit'
  | 'credit_transactions:delete'
  
  // Customer Entitlements
  | 'customer_entitlements:view:all'
  | 'customer_entitlements:create'
  | 'customer_entitlements:edit'
  | 'customer_entitlements:delete'
  | 'entitlements:create'
  | 'entitlements:view'
  | 'entitlements:view:own'
  | 'entitlements:view:all'
  | 'entitlements:edit'
  | 'entitlements:delete'
  | 'entitlements:manage'
  
  // Discount Codes
  | 'discount_codes:view:all'
  | 'discount_codes:create'
  | 'discount_codes:edit'
  | 'discount_codes:delete'
  
  // Events & RSVP & Notifications & Comments
  | 'event:create'
  | 'event:create:custom'
  | 'event:view'
  | 'event:edit'
  | 'event:edit:location'
  | 'event:delete'
  | 'event:delete:custom'
  | 'event:manage_rsvp'
  | 'event_comments:view:all'
  | 'event_comments:create'
  | 'event_comments:edit'
  | 'event_comments:delete'
  | 'event_notifications:view:all'
  | 'event_notifications:create'
  | 'event_notifications:edit'
  | 'event_notifications:delete'
  | 'event_rsvp:view:all'
  | 'event_rsvp:create'
  | 'event_rsvp:edit'
  | 'event_rsvp:delete'
  
  // Family (Members, Relationships, Link Requests, Settings)
  | 'family_tree:create'
  | 'family_tree:view'
  | 'family_tree:edit'
  | 'family_tree:delete'
  | 'family_tree:export'
  | 'family_tree:import'
  | 'family_tree:manage:all'
  | 'family_link_requests:view:all'
  | 'family_link_requests:create'
  | 'family_link_requests:edit'
  | 'family_link_requests:delete'
  | 'family_members:view:all'
  | 'family_members:create'
  | 'family_members:edit'
  | 'family_members:delete'
  | 'family_relationships:view:all'
  | 'family_relationships:create'
  | 'family_relationships:edit'
  | 'family_relationships:delete'
  | 'family_section_settings:view:all'
  | 'family_section_settings:create'
  | 'family_section_settings:edit'
  | 'family_section_settings:delete'
  
  // Invoices
  | 'invoice:create'
  | 'invoice:view'
  | 'invoice:view:all'
  | 'invoice:edit'
  | 'invoice:generate_sds_collective'
  | 'invoice:generate_bestatter_collective'
  | 'invoice:generate_b2b'
  | 'invoice:generate_pdf'
  | 'invoice:send_email'
  | 'invoices:view:all'
  | 'invoices:create'
  | 'invoices:edit'
  | 'invoices:delete'
  
  // Media
  | 'media:upload'
  | 'media:view'
  | 'media:view:all'
  | 'media:create'
  | 'media:edit'
  | 'media:delete'
  | 'media:manage'
  
  // Memberships
  | 'memberships:view:all'
  | 'memberships:create'
  | 'memberships:edit'
  | 'memberships:delete'
  
  // Memorial Albums & Access & Comments & Downloads & Images
  | 'memorial_album_access:view:all'
  | 'memorial_album_access:create'
  | 'memorial_album_access:edit'
  | 'memorial_album_access:delete'
  | 'memorial_album_comments:view:all'
  | 'memorial_album_comments:create'
  | 'memorial_album_comments:edit'
  | 'memorial_album_comments:delete'
  | 'memorial_album_downloads:view:all'
  | 'memorial_album_downloads:create'
  | 'memorial_album_downloads:edit'
  | 'memorial_album_downloads:delete'
  | 'memorial_album_images:view:all'
  | 'memorial_album_images:create'
  | 'memorial_album_images:edit'
  | 'memorial_album_images:delete'
  | 'memorial_albums:view:all'
  | 'memorial_albums:create'
  | 'memorial_albums:edit'
  | 'memorial_albums:delete'
  
  // Memorial Collaborators
  | 'memorial:add_collaborator'
  | 'memorial:remove_collaborator'
  | 'memorial:manage_collaborators'
  | 'memorial_collaborators:view:all'
  | 'memorial_collaborators:create'
  | 'memorial_collaborators:edit'
  | 'memorial_collaborators:delete'
  
  // Memorial Events
  | 'memorial_events:view:all'
  | 'memorial_events:create'
  | 'memorial_events:edit'
  | 'memorial_events:delete'
  
  // Memorial Pages & Videos & Albums & Items & Access
  | 'memorial_page_album_access:view:all'
  | 'memorial_page_album_access:create'
  | 'memorial_page_album_access:edit'
  | 'memorial_page_album_access:delete'
  | 'memorial_page_album_items:view:all'
  | 'memorial_page_album_items:create'
  | 'memorial_page_album_items:edit'
  | 'memorial_page_album_items:delete'
  | 'memorial_page_albums:view:all'
  | 'memorial_page_albums:create'
  | 'memorial_page_albums:edit'
  | 'memorial_page_albums:delete'
  | 'memorial_page_videos:view:all'
  | 'memorial_page_videos:create'
  | 'memorial_page_videos:edit'
  | 'memorial_page_videos:delete'
  | 'memorial_pages:view:all'
  | 'memorial_pages:create'
  | 'memorial_pages:edit'
  | 'memorial_pages:delete'
  
  // Memorial Visitor Uploads
  | 'memorial_visitor_uploads:view:all'
  | 'memorial_visitor_uploads:create'
  | 'memorial_visitor_uploads:moderate'
  | 'memorial_visitor_uploads:delete'
  
  // Memorial Core permissions
  | 'memorial:create'
  | 'memorial:view'
  | 'memorial:view:all'
  | 'memorial:edit'
  | 'memorial:edit:all'
  | 'memorial:delete'
  | 'memorial:delete:all'
  | 'memorial:reassign'
  | 'memorial:reassign:all'
  | 'memorial:extend'
  | 'memorial:extend:manage'
  | 'memorial:view:expired'
  | 'memorial:edit:time_restricted'
  
  // Memorial Groups
  | 'memorial_group:create'
  | 'memorial_group:view'
  | 'memorial_group:view:all'
  | 'memorial_group:edit'
  | 'memorial_group:delete'
  | 'memorial_group:add_member'
  | 'memorial_group:remove_member'
  | 'memorial_group:manage_permissions'
  
  // Memory Lights
  | 'memory_light:create'
  | 'memory_light:create:own'
  | 'memory_light:create:sds'
  | 'memory_light:create:unlimited'
  | 'memory_light:upload_media'
  | 'memory_light:approve'
  | 'memory_light:moderate'
  | 'memory_light:delete'
  | 'memory_light:view_unapproved'
  | 'memory_light:view:all'
  | 'memory_light:edit:all'
  | 'memory_light:delete:all'
  | 'memory_lights:manage'
  | 'memory_lights:moderate'
  | 'memory_lights:delete'
  | 'memory_lights:view'
  | 'memory_lights:create'
  
  // Gallery Albums
  | 'gallery:create_album'
  | 'gallery:view_private'
  | 'gallery:manage_album'
  | 'gallery:delete_album'
  | 'gallery:set_password'
  | 'gallery:upload'
  | 'gallery:download'
  | 'gallery:manage:all'
  
  // Plaque
  | 'plaque:create'
  | 'plaque:view'
  | 'plaque:edit'
  | 'plaque:delete'
  | 'plaque:view:all'

  // Laser Orders (SDS)
  | 'laser:order:create'
  | 'laser:order:view'
  | 'laser:order:edit'
  | 'laser:order:ship' // Kreuzmal: Sendungsnummer setzen + Status auf "shipped" setzen
  
  // Roles & System Roles
  | 'roles:view:all'
  | 'roles:manage'
  | 'system_roles:manage'
  
  // Shipping Rates
  | 'shipping_rates:manage'
  | 'shipping_rates:create'
  | 'shipping_rates:edit'
  | 'shipping_rates:delete'
  
  // Subscriptions
  | 'subscriptions:view'
  | 'subscriptions:create'
  | 'subscriptions:edit'
  | 'subscriptions:delete'
  
  // User Management
  | 'user:create'
  | 'user:view'
  | 'user:view:all'
  | 'user:edit'
  | 'user:delete'
  | 'user:check_email'
  
  // Branch Management
  | 'branch:create'
  | 'branch:view'
  | 'branch:view:all'
  | 'branch:edit'
  | 'branch:delete'
  
  // Order/Billing
  | 'order:create'
  | 'order:view'
  | 'order:view:all'
  | 'order:edit'
  | 'order:delete'
  
  // Product
  | 'product:create'
  | 'product:view'
  | 'product:view:all'
  | 'product:edit'
  | 'product:delete'
  
  // Shop
  | 'shop:access'
  | 'shop:cart:manage'
  | 'shop:purchase'
  | 'shop:checkout'
  
  // Embed
  | 'embed:generate'
  
  // Report
  | 'view_reports'
  
  // E-Mail
  | 'email:plaque_status_send'
  | 'email:plaque_order_admin_notification'
  | 'email:password_send'
  
  // Moderation
  | 'moderation:view_queue'
  | 'moderation:approve'
  | 'moderation:reject'
  | 'moderation:manage:all'
  
  // AI
  | 'ai:memorial-suggestion'
  
  // Auth
  | 'auth:login'
  | 'auth:register'
  | 'auth:login:google'
  | 'auth:login:facebook'

    // Impersonation
  | 'user:impersonate'

  // Directory
  | 'directory:access'
  | 'directory:provider:view'
  | 'directory:provider:create'
  | 'directory:provider:edit'
  | 'directory:provider:delete'
  | 'directory:category:view'
  | 'directory:category:create'
  | 'directory:category:edit'
  | 'directory:category:delete'
  | 'directory:item:view'
  | 'directory:item:create'
  | 'directory:item:edit'
  | 'directory:item:delete'
  
  // SDS Specific Permissions
  | 'sds:user'
  | 'sds:user:view'
  | 'sds:user:edit'
  | 'sds:user:delete'
  | 'sds:user:create'
  | 'sds:branch'
  | 'sds:branch:view'
  | 'sds:branch:edit'
  | 'sds:branch:delete'
  | 'sds:branch:create'
  | 'sds:bestatter:view'
  | 'sds:bestatter:edit'
  | 'sds:bestatter:delete'
  | 'sds:bestatter:create'
  | 'sds:bestatter:read'
  | 'sds:bestatter:update'
  | 'sds:invoice'
  | 'sds:invoice:view'
  | 'sds:invoice:edit'
  | 'sds:invoice:create'
  | 'sds:customer'
  | 'sds:customer:view'
  | 'sds:customer:edit'
  | 'sds:customer:create'
  | 'sds:customer:delete'
  | 'sds:stats'
  | 'sds:stats:view'
  | 'sds:document'
  | 'sds:document:view'
  | 'sds:document:create'
  | 'sds:document:edit'
  | 'sds:document:delete'
  | 'sds:case'
  | 'sds:case:view'
  | 'sds:case:edit'
  | 'sds:case:create'
  | 'sds:case:delete'
  | 'sds:memorial'
  | 'sds:memorial:view'
  | 'sds:memorial:edit'
  | 'sds:memorial:create'
  | 'sds:memorial:delete'
  | 'sds:bulk_invoices:view'
  | 'sds:memory_light:view'
  | 'sds:memory_light:create'
  | 'sds:memory_light:edit'
  | 'sds:memory_light:delete'
  | 'sds:event:view'
  | 'sds:event:create'
  | 'sds:event:edit'
  | 'sds:event:delete'
  | 'sds:gallery:view'
  | 'sds:gallery:create_album'
  | 'sds:gallery:manage_album'
  | 'sds:gallery:delete_album'
  | 'sds:gallery:upload'
  | 'sds:gallery:download'
  | 'sds:gallery:set_password'
  | 'sds:family_tree:view'
  | 'sds:family_tree:create'
  | 'sds:family_tree:edit'
  | 'sds:family_tree:delete'
  | 'sds:family_tree:export'
  | 'sds:family_tree:import'
  | 'sds:collaboration:view'
  | 'sds:collaboration:invite'
  | 'sds:collaboration:manage'
  | 'sds:collaboration:remove'
  | 'sds:employee:view'
  | 'sds:employee:create'
  | 'sds:employee:edit'
  | 'sds:employee:delete'
  | 'sds:admin'
   | 'sds:export:exclusive'  // NUR SDS-Exporte, keine anderen

  // Donations (Stripe Connect)
  | 'donation:manage'
  | 'donation:view:all'
;

// Permission value object for easy usage as enum-like value
export const Permission = {
  // System
  AdminDashboardAccess: 'admin:dashboard:access',

  // Admin Dashboard Navigation Permissions (NEU)
  AdminNavDashboard: 'admin:nav:dashboard',
  AdminNavUserDashboard: 'admin:nav:user_dashboard',
  AdminNavNewMemorial: 'admin:nav:new_memorial',
  AdminNavAllMemorials: 'admin:nav:all_memorials',
  AdminNavAlbums: 'admin:nav:albums',
  AdminNavEvents: 'admin:nav:events',
  AdminNavFamilyTrees: 'admin:nav:family_trees',
  AdminNavCollaborations: 'admin:nav:collaborations',
  AdminNavMemoryLights: 'admin:nav:memory_lights',
  AdminNavUsers: 'admin:nav:users',
  AdminNavPermissions: 'admin:nav:permissions',
  AdminNavBestatter: 'admin:nav:bestatter',
  AdminNavBranches: 'admin:nav:branches',
  AdminNavBranchesStructure: 'admin:nav:branches_structure',
  AdminNavEmbedCode: 'admin:nav:embed_code',
  AdminNavSdsInvoices: 'admin:nav:sds_invoices',
  AdminNavSdsApiKeys: 'admin:nav:sds_api_keys',
  AdminNavCreditsPackages: 'admin:nav:credits_packages',
  AdminNavCreditsTransfer: 'admin:nav:credits_transfer',
  AdminNavCreditsTransactions: 'admin:nav:credits_transactions',
  AdminNavQuotaUsage: 'admin:nav:quota_usage',
  AdminNavProducts: 'admin:nav:products',
  AdminNavBundles: 'admin:nav:bundles',
  AdminNavMerchone: 'admin:nav:merchone',
  AdminNavMerchoneCatalog: 'admin:nav:merchone_catalog',
  AdminNavMerchoneSync: 'admin:nav:merchone_sync',
  AdminNavMerchoneShop: 'admin:nav:merchone_shop',
  AdminNavOrders: 'admin:nav:orders',
  AdminNavInvoices: 'admin:nav:invoices',
  AdminNavPlaques: 'admin:nav:plaques',
  AdminNavQrcodes: 'admin:nav:qrcodes',
  
  // Accounts
  AccountsViewAll: 'accounts:view:all',
  AccountsCreate: 'accounts:create',
  AccountsEdit: 'accounts:edit',
  AccountsDelete: 'accounts:delete',
  
  // Activity Logs
  ActivityLogsViewAll: 'activity_logs:view:all',
  ActivityLogsCreate: 'activity_logs:create',
  ActivityLogsEdit: 'activity_logs:edit',
  ActivityLogsDelete: 'activity_logs:delete',
  
  // API Keys & Request Logs
  ApiKeysViewAll: 'api_keys:view:all',
  ApiKeysCreate: 'api_keys:create',
  ApiKeysEdit: 'api_keys:edit',
  ApiKeysDelete: 'api_keys:delete',
  ApiRequestLogsViewAll: 'api_request_logs:view:all',
  ApiRequestLogsCreate: 'api_request_logs:create',
  ApiRequestLogsEdit: 'api_request_logs:edit',
  ApiRequestLogsDelete: 'api_request_logs:delete',
  ApiKeyCreate: 'apikey:create',
  ApiKeyEdit: 'apikey:edit',
  ApiKeyDelete: 'apikey:delete',
  ApiKeyView: 'apikey:view',
  ApiKeyViewAll: 'apikey:view:all',
  
  // Bestatter Branches
  BestatterBranchesViewAll: 'bestatter_branches:view:all',
  BestatterBranchesCreate: 'bestatter_branches:create',
  BestatterBranchesEdit: 'bestatter_branches:edit',
  BestatterBranchesDelete: 'bestatter_branches:delete',
  
  // Bulk Operations
  BulkOperationsViewAll: 'bulk_operations:view:all',
  BulkOperationsCreate: 'bulk_operations:create',
  BulkOperationsEdit: 'bulk_operations:edit',
  BulkOperationsDelete: 'bulk_operations:delete',
  
  // Cart Items
  CartItemsViewAll: 'cart_items:view:all',
  CartItemsCreate: 'cart_items:create',
  CartItemsEdit: 'cart_items:edit',
  CartItemsDelete: 'cart_items:delete',
  
  // Collaboration & Invitations
  CollaborationInvite: 'collaboration:invite',
  CollaborationManage: 'collaboration:manage',
  CollaborationRemove: 'collaboration:remove',
  CollaborationView: 'collaboration:view',
  CollaborationInvitationsViewAll: 'collaboration_invitations:view:all',
  CollaborationInvitationsCreate: 'collaboration_invitations:create',
  CollaborationInvitationsEdit: 'collaboration_invitations:edit',
  CollaborationInvitationsDelete: 'collaboration_invitations:delete',
  CollaboratorActivityLogsViewAll: 'collaborator_activity_logs:view:all',
  CollaboratorActivityLogsCreate: 'collaborator_activity_logs:create',
  CollaboratorActivityLogsEdit: 'collaborator_activity_logs:edit',
  CollaboratorActivityLogsDelete: 'collaborator_activity_logs:delete',
  AccountAddMember: 'account:add_member',
  AccountRemoveMember: 'account:remove_member',
  AccountManagePermissions: 'account:manage_permissions',
  
  // Comments
  CommentsViewAll: 'comments:view:all',
  CommentsCreate: 'comments:create',
  CommentsEdit: 'comments:edit',
  CommentsDelete: 'comments:delete',
  
  // Credits & Packages & Transactions
  CreditRead: 'credit:read',
  CreditPurchase: 'credit:purchase',
  CreditsView: 'credits:view',
  CreditsEdit: 'credits:edit',
  CreditsDelete: 'credits:delete',
  CreditsManage: 'credits:manage',
  CreditPackagesViewAll: 'credit_packages:view:all',
  CreditPackagesCreate: 'credit_packages:create',
  CreditPackagesEdit: 'credit_packages:edit',
  CreditPackagesDelete: 'credit_packages:delete',
  CreditTransactionsViewAll: 'credit_transactions:view:all',
  CreditTransactionsCreate: 'credit_transactions:create',
  CreditTransactionsEdit: 'credit_transactions:edit',
  CreditTransactionsDelete: 'credit_transactions:delete',
  
  // Customer Entitlements
  CustomerEntitlementsViewAll: 'customer_entitlements:view:all',
  CustomerEntitlementsCreate: 'customer_entitlements:create',
  CustomerEntitlementsEdit: 'customer_entitlements:edit',
  CustomerEntitlementsDelete: 'customer_entitlements:delete',
  EntitlementsCreate: 'entitlements:create',
  EntitlementsView: 'entitlements:view',
  EntitlementsViewOwn: 'entitlements:view:own',
  EntitlementsViewAll: 'entitlements:view:all',
  EntitlementsEdit: 'entitlements:edit',
  EntitlementsDelete: 'entitlements:delete',
  EntitlementsManage: 'entitlements:manage',
  
  // Discount Codes
  DiscountCodesViewAll: 'discount_codes:view:all',
  DiscountCodesCreate: 'discount_codes:create',
  DiscountCodesEdit: 'discount_codes:edit',
  DiscountCodesDelete: 'discount_codes:delete',

    // Directory
  DirectoryAccess: 'directory:access',

  DirectoryProviderView: 'directory:provider:view',
  DirectoryProviderCreate: 'directory:provider:create',
  DirectoryProviderEdit: 'directory:provider:edit',
  DirectoryProviderDelete: 'directory:provider:delete',

  DirectoryCategoryView: 'directory:category:view',
  DirectoryCategoryCreate: 'directory:category:create',
  DirectoryCategoryEdit: 'directory:category:edit',
  DirectoryCategoryDelete: 'directory:category:delete',

  DirectoryItemView: 'directory:item:view',
  DirectoryItemCreate: 'directory:item:create',
  DirectoryItemEdit: 'directory:item:edit',
  DirectoryItemDelete: 'directory:item:delete',
  
  // Events & RSVP & Notifications & Comments
  EventCreate: 'event:create',
  EventCreateCustom: 'event:create:custom',
  EventView: 'event:view',
  EventEdit: 'event:edit',
  EventEditLocation: 'event:edit:location',
  EventDelete: 'event:delete',
  EventDeleteCustom: 'event:delete:custom',
  EventManageRsvp: 'event:manage_rsvp',
  EventCommentsViewAll: 'event_comments:view:all',
  EventCommentsCreate: 'event_comments:create',
  EventCommentsEdit: 'event_comments:edit',
  EventCommentsDelete: 'event_comments:delete',
  EventNotificationsViewAll: 'event_notifications:view:all',
  EventNotificationsCreate: 'event_notifications:create',
  EventNotificationsEdit: 'event_notifications:edit',
  EventNotificationsDelete: 'event_notifications:delete',
  EventRsvpViewAll: 'event_rsvp:view:all',
  EventRsvpCreate: 'event_rsvp:create',
  EventRsvpEdit: 'event_rsvp:edit',
  EventRsvpDelete: 'event_rsvp:delete',
  
  // Family (Members, Relationships, Link Requests, Settings)
  FamilyTreeCreate: 'family_tree:create',
  FamilyTreeView: 'family_tree:view',
  FamilyTreeEdit: 'family_tree:edit',
  FamilyTreeDelete: 'family_tree:delete',
  FamilyTreeExport: 'family_tree:export',
  FamilyTreeImport: 'family_tree:import',
  FamilyTreeManageAll: 'family_tree:manage:all',
  FamilyLinkRequestsViewAll: 'family_link_requests:view:all',
  FamilyLinkRequestsCreate: 'family_link_requests:create',
  FamilyLinkRequestsEdit: 'family_link_requests:edit',
  FamilyLinkRequestsDelete: 'family_link_requests:delete',
  FamilyMembersViewAll: 'family_members:view:all',
  FamilyMembersCreate: 'family_members:create',
  FamilyMembersEdit: 'family_members:edit',
  FamilyMembersDelete: 'family_members:delete',
  FamilyRelationshipsViewAll: 'family_relationships:view:all',
  FamilyRelationshipsCreate: 'family_relationships:create',
  FamilyRelationshipsEdit: 'family_relationships:edit',
  FamilyRelationshipsDelete: 'family_relationships:delete',
  FamilySectionSettingsViewAll: 'family_section_settings:view:all',
  FamilySectionSettingsCreate: 'family_section_settings:create',
  FamilySectionSettingsEdit: 'family_section_settings:edit',
  FamilySectionSettingsDelete: 'family_section_settings:delete',
  
  // Invoices
  InvoiceCreate: 'invoice:create',
  InvoiceView: 'invoice:view',
  InvoiceViewAll: 'invoice:view:all',
  InvoiceEdit: 'invoice:edit',
  InvoiceGenerateSDSCollective: 'invoice:generate_sds_collective',
  InvoiceGenerateBestatterCollective: 'invoice:generate_bestatter_collective',
  InvoiceGenerateB2B: 'invoice:generate_b2b',
  InvoiceGeneratePDF: 'invoice:generate_pdf',
  InvoiceSendEmail: 'invoice:send_email',
  InvoicesViewAll: 'invoices:view:all',
  InvoicesCreate: 'invoices:create',
  InvoicesEdit: 'invoices:edit',
  InvoicesDelete: 'invoices:delete',
  
  // Media
  MediaUpload: 'media:upload',
  MediaView: 'media:view',
  MediaViewAll: 'media:view:all',
  MediaCreate: 'media:create',
  MediaEdit: 'media:edit',
  MediaDelete: 'media:delete',
  MediaManage: 'media:manage',
  
  // Memberships
  MembershipsViewAll: 'memberships:view:all',
  MembershipsCreate: 'memberships:create',
  MembershipsEdit: 'memberships:edit',
  MembershipsDelete: 'memberships:delete',
  
  // Memorial Albums & Access & Comments & Downloads & Images
  MemorialAlbumAccessViewAll: 'memorial_album_access:view:all',
  MemorialAlbumAccessCreate: 'memorial_album_access:create',
  MemorialAlbumAccessEdit: 'memorial_album_access:edit',
  MemorialAlbumAccessDelete: 'memorial_album_access:delete',
  MemorialAlbumCommentsViewAll: 'memorial_album_comments:view:all',
  MemorialAlbumCommentsCreate: 'memorial_album_comments:create',
  MemorialAlbumCommentsEdit: 'memorial_album_comments:edit',
  MemorialAlbumCommentsDelete: 'memorial_album_comments:delete',
  MemorialAlbumDownloadsViewAll: 'memorial_album_downloads:view:all',
  MemorialAlbumDownloadsCreate: 'memorial_album_downloads:create',
  MemorialAlbumDownloadsEdit: 'memorial_album_downloads:edit',
  MemorialAlbumDownloadsDelete: 'memorial_album_downloads:delete',
  MemorialAlbumImagesViewAll: 'memorial_album_images:view:all',
  MemorialAlbumImagesCreate: 'memorial_album_images:create',
  MemorialAlbumImagesEdit: 'memorial_album_images:edit',
  MemorialAlbumImagesDelete: 'memorial_album_images:delete',
  MemorialAlbumsViewAll: 'memorial_albums:view:all',
  MemorialAlbumsCreate: 'memorial_albums:create',
  MemorialAlbumsEdit: 'memorial_albums:edit',
  MemorialAlbumsDelete: 'memorial_albums:delete',
  
  // Memorial Collaborators
  MemorialAddCollaborator: 'memorial:add_collaborator',
  MemorialRemoveCollaborator: 'memorial:remove_collaborator',
  MemorialManageCollaborators: 'memorial:manage_collaborators',
  MemorialCollaboratorsViewAll: 'memorial_collaborators:view:all',
  MemorialCollaboratorsCreate: 'memorial_collaborators:create',
  MemorialCollaboratorsEdit: 'memorial_collaborators:edit',
  MemorialCollaboratorsDelete: 'memorial_collaborators:delete',
  
  // Memorial Events
  MemorialEventsViewAll: 'memorial_events:view:all',
  MemorialEventsCreate: 'memorial_events:create',
  MemorialEventsEdit: 'memorial_events:edit',
  MemorialEventsDelete: 'memorial_events:delete',
  
  // Memorial Pages & Videos & Albums & Items & Access
  MemorialPageAlbumAccessViewAll: 'memorial_page_album_access:view:all',
  MemorialPageAlbumAccessCreate: 'memorial_page_album_access:create',
  MemorialPageAlbumAccessEdit: 'memorial_page_album_access:edit',
  MemorialPageAlbumAccessDelete: 'memorial_page_album_access:delete',
  MemorialPageAlbumItemsViewAll: 'memorial_page_album_items:view:all',
  MemorialPageAlbumItemsCreate: 'memorial_page_album_items:create',
  MemorialPageAlbumItemsEdit: 'memorial_page_album_items:edit',
  MemorialPageAlbumItemsDelete: 'memorial_page_album_items:delete',
  MemorialPageAlbumsViewAll: 'memorial_page_albums:view:all',
  MemorialPageAlbumsCreate: 'memorial_page_albums:create',
  MemorialPageAlbumsEdit: 'memorial_page_albums:edit',
  MemorialPageAlbumsDelete: 'memorial_page_albums:delete',
  MemorialPageVideosViewAll: 'memorial_page_videos:view:all',
  MemorialPageVideosCreate: 'memorial_page_videos:create',
  MemorialPageVideosEdit: 'memorial_page_videos:edit',
  MemorialPageVideosDelete: 'memorial_page_videos:delete',
  MemorialPagesViewAll: 'memorial_pages:view:all',
  MemorialPagesCreate: 'memorial_pages:create',
  MemorialPagesEdit: 'memorial_pages:edit',
  MemorialPagesDelete: 'memorial_pages:delete',
  
  // Memorial Visitor Uploads
  MemorialVisitorUploadsViewAll: 'memorial_visitor_uploads:view:all',
  MemorialVisitorUploadsCreate: 'memorial_visitor_uploads:create',
  MemorialVisitorUploadsModerate: 'memorial_visitor_uploads:moderate',
  MemorialVisitorUploadsDelete: 'memorial_visitor_uploads:delete',
  
  // Memorial Core permissions
  MemorialCreate: 'memorial:create',
  MemorialView: 'memorial:view',
  MemorialViewAll: 'memorial:view:all',
  MemorialEdit: 'memorial:edit',
  MemorialEditAll: 'memorial:edit:all',
  MemorialDelete: 'memorial:delete',
  MemorialDeleteAll: 'memorial:delete:all',
  MemorialReassign: 'memorial:reassign',
  MemorialReassignAll: 'memorial:reassign:all',
  MemorialExtend: 'memorial:extend',
  MemorialExtendManage: 'memorial:extend:manage',
  MemorialViewExpired: 'memorial:view:expired',
  MemorialEditTimeRestricted: 'memorial:edit:time_restricted',
  
  // Memorial Groups
  MemorialGroupCreate: 'memorial_group:create',
  MemorialGroupView: 'memorial_group:view',
  MemorialGroupViewAll: 'memorial_group:view:all',
  MemorialGroupEdit: 'memorial_group:edit',
  MemorialGroupDelete: 'memorial_group:delete',
  MemorialGroupAddMember: 'memorial_group:add_member',
  MemorialGroupRemoveMember: 'memorial_group:remove_member',
  MemorialGroupManagePermissions: 'memorial_group:manage_permissions',
  
  // Memory Lights
  MemoryLightCreate: 'memory_light:create',
  MemoryLightCreateOwn: 'memory_light:create:own',
  MemoryLightCreateSds: 'memory_light:create:sds',
  MemoryLightCreateUnlimited: 'memory_light:create:unlimited',
  MemoryLightUploadMedia: 'memory_light:upload_media',
  MemoryLightApprove: 'memory_light:approve',
  MemoryLightModerate: 'memory_light:moderate',
  MemoryLightDelete: 'memory_light:delete',
  MemoryLightViewUnapproved: 'memory_light:view_unapproved',
  MemoryLightViewAll: 'memory_light:view:all',
  MemoryLightEditAll: 'memory_light:edit:all',
  MemoryLightDeleteAll: 'memory_light:delete:all',
  MemoryLightsManage: 'memory_lights:manage',
  MemoryLightsModerate: 'memory_lights:moderate',
  MemoryLightsDelete: 'memory_lights:delete',
  MemoryLightsView: 'memory_lights:view',
  MemoryLightsCreate: 'memory_lights:create',
  
  // Gallery Albums
  GalleryCreateAlbum: 'gallery:create_album',
  GalleryViewPrivate: 'gallery:view_private',
  GalleryManageAlbum: 'gallery:manage_album',
  GalleryDeleteAlbum: 'gallery:delete_album',
  GallerySetPassword: 'gallery:set_password',
  GalleryUpload: 'gallery:upload',
  GalleryDownload: 'gallery:download',
  GalleryManageAll: 'gallery:manage:all',
  
  // Plaque
  PlaqueCreate: 'plaque:create',
  PlaqueView: 'plaque:view',
  PlaqueEdit: 'plaque:edit',
  PlaqueDelete: 'plaque:delete',
  PlaqueViewAll: 'plaque:view:all',

  // Laser Orders (SDS)
  LaserOrderCreate: 'laser:order:create',
  LaserOrderView: 'laser:order:view',
  LaserOrderEdit: 'laser:order:edit',
  LaserOrderShip: 'laser:order:ship',
  
  // Roles & System Roles
  RolesViewAll: 'roles:view:all',
  RolesManage: 'roles:manage',
  SystemRolesManage: 'system_roles:manage',
  
  // Shipping Rates
  ShippingRatesManage: 'shipping_rates:manage',
  ShippingRatesCreate: 'shipping_rates:create',
  ShippingRatesEdit: 'shipping_rates:edit',
  ShippingRatesDelete: 'shipping_rates:delete',
  
  // Subscriptions
  SubscriptionsView: 'subscriptions:view',
  SubscriptionsCreate: 'subscriptions:create',
  SubscriptionsEdit: 'subscriptions:edit',
  SubscriptionsDelete: 'subscriptions:delete',
  
  // User Management
  UserCreate: 'user:create',
  UserView: 'user:view',
  UserViewAll: 'user:view:all',
  UserEdit: 'user:edit',
  UserDelete: 'user:delete',
  CheckEmail: 'user:check_email',
  
  // Branch Management
  BranchCreate: 'branch:create',
  BranchView: 'branch:view',
  BranchViewAll: 'branch:view:all',
  BranchEdit: 'branch:edit',
  BranchDelete: 'branch:delete',
  
  // Order/Billing
  OrderCreate: 'order:create',
  OrderView: 'order:view',
  OrderViewAll: 'order:view:all',
  OrderEdit: 'order:edit',
  OrderDelete: 'order:delete',
  
  // Product
  ProductCreate: 'product:create',
  ProductView: 'product:view',
  ProductViewAll: 'product:view:all',
  ProductEdit: 'product:edit',
  ProductDelete: 'product:delete',
  
  // Shop
  ShopAccess: 'shop:access',
  ShopCartManage: 'shop:cart:manage',
  ShopPurchase: 'shop:purchase',
  ShopCheckout: 'shop:checkout',
  
  // Embed
  EmbedGenerate: 'embed:generate',
  
  // Report
  ViewReports: 'view_reports',
  
  // E-Mail
  EmailPlaqueStatusSend: 'email:plaque_status_send',
  EmailPlaqueOrderAdminNotification: 'email:plaque_order_admin_notification',
  EmailPasswordSend: 'email:password_send',
  
  // Moderation
  ModerationViewQueue: 'moderation:view_queue',
  ModerationApprove: 'moderation:approve',
  ModerationReject: 'moderation:reject',
  ModerationManageAll: 'moderation:manage:all',
  
  // AI
  AIMemorialSuggestion: 'ai:memorial-suggestion',
  
  // Auth
  AuthLogin: 'auth:login',
  AuthRegister: 'auth:register',
  AuthLoginGoogle: 'auth:login:google',
  AuthLoginFacebook: 'auth:login:facebook',

  // Impersonation
  UserImpersonate: 'user:impersonate',
  
  // SDS Specific Permissions
  SDSUser: 'sds:user',
  SDSUserView: 'sds:user:view',
  SDSUserEdit: 'sds:user:edit',
  SDSUserDelete: 'sds:user:delete',
  SDSUserCreate: 'sds:user:create',
  SDSBranch: 'sds:branch',
  SDSBranchView: 'sds:branch:view',
  SDSBranchEdit: 'sds:branch:edit',
  SDSBranchDelete: 'sds:branch:delete',
  SDSBranchCreate: 'sds:branch:create',
  SDSBestatterView: 'sds:bestatter:view',
  SDSBestatterEdit: 'sds:bestatter:edit',
  SDSBestatterDelete: 'sds:bestatter:delete',
  SDSBestatterCreate: 'sds:bestatter:create',
  SDSBestatterRead: 'sds:bestatter:read',
  SDSBestatterUpdate: 'sds:bestatter:update',
  SDSInvoice: 'sds:invoice',
  SDSInvoiceView: 'sds:invoice:view',
  SDSInvoiceEdit: 'sds:invoice:edit',
  SDSInvoiceCreate: 'sds:invoice:create',
  SDSCustomer: 'sds:customer',
  SDSCustomerView: 'sds:customer:view',
  SDSCustomerEdit: 'sds:customer:edit',
  SDSCustomerCreate: 'sds:customer:create',
  SDSCustomerDelete: 'sds:customer:delete',
  SDSStats: 'sds:stats',
  SDSStatsView: 'sds:stats:view',
  SDSDocument: 'sds:document',
  SDSDocumentView: 'sds:document:view',
  SDSDocumentCreate: 'sds:document:create',
  SDSDocumentEdit: 'sds:document:edit',
  SDSDocumentDelete: 'sds:document:delete',
  SDSCase: 'sds:case',
  SDSCaseView: 'sds:case:view',
  SDSCaseEdit: 'sds:case:edit',
  SDSCaseCreate: 'sds:case:create',
  SDSCaseDelete: 'sds:case:delete',
  SDSMemorial: 'sds:memorial',
  SDSMemorialView: 'sds:memorial:view',
  SDSMemorialEdit: 'sds:memorial:edit',
  SDSMemorialCreate: 'sds:memorial:create',
  SDSMemorialDelete: 'sds:memorial:delete',
  SDSBulkInvoicesView: 'sds:bulk_invoices:view',
  SDSMemoryLightView: 'sds:memory_light:view',
  SDSMemoryLightCreate: 'sds:memory_light:create',
  SDSMemoryLightEdit: 'sds:memory_light:edit',
  SDSMemoryLightDelete: 'sds:memory_light:delete',
  SDSEventView: 'sds:event:view',
  SDSEventCreate: 'sds:event:create',
  SDSEventEdit: 'sds:event:edit',
  SDSEventDelete: 'sds:event:delete',
  SDSGalleryView: 'sds:gallery:view',
  SDSGalleryCreateAlbum: 'sds:gallery:create_album',
  SDSGalleryManageAlbum: 'sds:gallery:manage_album',
  SDSGalleryDeleteAlbum: 'sds:gallery:delete_album',
  SDSGalleryUpload: 'sds:gallery:upload',
  SDSGalleryDownload: 'sds:gallery:download',
  SDSGallerySetPassword: 'sds:gallery:set_password',
  SDSFamilyTreeView: 'sds:family_tree:view',
  SDSFamilyTreeCreate: 'sds:family_tree:create',
  SDSFamilyTreeEdit: 'sds:family_tree:edit',
  SDSFamilyTreeDelete: 'sds:family_tree:delete',
  SDSFamilyTreeExport: 'sds:family_tree:export',
  SDSFamilyTreeImport: 'sds:family_tree:import',
  SDSCollaborationView: 'sds:collaboration:view',
  SDSCollaborationInvite: 'sds:collaboration:invite',
  SDSCollaborationManage: 'sds:collaboration:manage',
  SDSCollaborationRemove: 'sds:collaboration:remove',
  SDSEmployeeView: 'sds:employee:view',
  SDSEmployeeCreate: 'sds:employee:create',
  SDSEmployeeEdit: 'sds:employee:edit',
  SDSEmployeeDelete: 'sds:employee:delete',
  SDSAdmin: 'sds:admin',
  SDSExportExclusive: 'sds:export:exclusive',
  
  // Donations (Stripe Connect)
  DonationManage: 'donation:manage',
  DonationViewAll: 'donation:view:all',
} as const;

/**
 * Vollständige Permission-Beschreibungen für alle Berechtigungen
 * Kategorisiert nach Bereichen mit Details zu Admin-Level und Funktion
 */
export const PERMISSION_DESCRIPTIONS: Record<Permission, { label: string; desc: string; adminLevel?: 'admin' | 'subadmin' | 'manager' | 'user' }> = {
  // System & Admin Dashboard
  'admin:dashboard:access': { 
    label: 'Admin Dashboard Zugang', 
    desc: 'Zugriff auf das Admin Dashboard (Admin-Bereich)', 
    adminLevel: 'admin' 
  },

  // Admin Dashboard Navigation
  'admin:nav:dashboard': { 
    label: 'Navigation: Dashboard', 
    desc: 'Dashboard im Admin-Menü anzeigen', 
    adminLevel: 'admin' 
  },
  'admin:nav:user_dashboard': { 
    label: 'Navigation: Benutzer Dashboard', 
    desc: 'Benutzer Dashboard im Admin-Menü anzeigen', 
    adminLevel: 'admin' 
  },
  'admin:nav:new_memorial': { 
    label: 'Navigation: Neue Gedenkseite', 
    desc: 'Neue Gedenkseite im Admin-Menü anzeigen', 
    adminLevel: 'manager' 
  },
  'admin:nav:all_memorials': { 
    label: 'Navigation: Alle Gedenkseiten', 
    desc: 'Alle Gedenkseiten im Admin-Menü anzeigen', 
    adminLevel: 'manager' 
  },
  'admin:nav:albums': { 
    label: 'Navigation: Alben', 
    desc: 'Alben-Verwaltung im Admin-Menü anzeigen', 
    adminLevel: 'manager' 
  },
  'admin:nav:events': { 
    label: 'Navigation: Events', 
    desc: 'Event-Verwaltung im Admin-Menü anzeigen', 
    adminLevel: 'manager' 
  },
  'admin:nav:family_trees': { 
    label: 'Navigation: Stammbäume', 
    desc: 'Stammbaum-Verwaltung im Admin-Menü anzeigen', 
    adminLevel: 'manager' 
  },
  'admin:nav:collaborations': { 
    label: 'Navigation: Kollaborationen', 
    desc: 'Kollaborationen im Admin-Menü anzeigen', 
    adminLevel: 'manager' 
  },
  'admin:nav:memory_lights': { 
    label: 'Navigation: Gedenklichter', 
    desc: 'Gedenklichter im Admin-Menü anzeigen', 
    adminLevel: 'manager' 
  },
  'admin:nav:users': { 
    label: 'Navigation: Benutzer', 
    desc: 'Benutzerverwaltung im Admin-Menü anzeigen', 
    adminLevel: 'admin' 
  },
  'admin:nav:permissions': { 
    label: 'Navigation: Berechtigungen', 
    desc: 'Berechtigungsverwaltung im Admin-Menü anzeigen', 
    adminLevel: 'admin' 
  },
  'admin:nav:bestatter': { 
    label: 'Navigation: Bestatter', 
    desc: 'Bestatter-Verwaltung im Admin-Menü anzeigen', 
    adminLevel: 'admin' 
  },
  'admin:nav:branches': { 
    label: 'Navigation: Filialen', 
    desc: 'Filialverwaltung im Admin-Menü anzeigen', 
    adminLevel: 'manager' 
  },
  'admin:nav:branches_structure': { 
    label: 'Navigation: Filialstruktur', 
    desc: 'Filialstruktur im Admin-Menü anzeigen', 
    adminLevel: 'admin' 
  },
  'admin:nav:embed_code': { 
    label: 'Navigation: Embed Code', 
    desc: 'Embed Code Generator im Admin-Menü anzeigen', 
    adminLevel: 'manager' 
  },
  'admin:nav:sds_invoices': { 
    label: 'Navigation: SDS Rechnungen', 
    desc: 'SDS Rechnungen im Admin-Menü anzeigen', 
    adminLevel: 'admin' 
  },
  'admin:nav:sds_api_keys': { 
    label: 'Navigation: SDS API Keys', 
    desc: 'SDS API Keys im Admin-Menü anzeigen', 
    adminLevel: 'admin' 
  },
  'admin:nav:credits_packages': { 
    label: 'Navigation: Credit Pakete', 
    desc: 'Credit-Pakete im Admin-Menü anzeigen', 
    adminLevel: 'admin' 
  },
  'admin:nav:credits_transfer': { 
    label: 'Navigation: Credit Transfer', 
    desc: 'Credit-Transfer im Admin-Menü anzeigen', 
    adminLevel: 'admin' 
  },
  'admin:nav:credits_transactions': { 
    label: 'Navigation: Credit Transaktionen', 
    desc: 'Credit-Transaktionen im Admin-Menü anzeigen', 
    adminLevel: 'admin' 
  },
  'admin:nav:quota_usage': {
    label: 'Navigation: Kontingentverbrauch',
    desc: 'Kontingentverbrauch (Abo-Gedenkseiten) im Admin-Menü anzeigen',
    adminLevel: 'admin'
  },
  'admin:nav:products': { 
    label: 'Navigation: Produkte', 
    desc: 'Produktverwaltung im Admin-Menü anzeigen', 
    adminLevel: 'admin' 
  },
  'admin:nav:bundles': { 
    label: 'Navigation: Bundles', 
    desc: 'Bundle-Verwaltung im Admin-Menü anzeigen', 
    adminLevel: 'admin' 
  },
  'admin:nav:merchone': { 
    label: 'Navigation: MerchOne', 
    desc: 'MerchOne Integration im Admin-Menü anzeigen', 
    adminLevel: 'admin' 
  },
  'admin:nav:merchone_catalog': { 
    label: 'Navigation: MerchOne Katalog', 
    desc: 'MerchOne Katalog im Admin-Menü anzeigen', 
    adminLevel: 'admin' 
  },
  'admin:nav:merchone_sync': { 
    label: 'Navigation: MerchOne Sync', 
    desc: 'MerchOne Synchronisation im Admin-Menü anzeigen', 
    adminLevel: 'admin' 
  },
  'admin:nav:merchone_shop': { 
    label: 'Navigation: MerchOne Shop', 
    desc: 'MerchOne Shop im Admin-Menü anzeigen', 
    adminLevel: 'admin' 
  },
  'admin:nav:orders': { 
    label: 'Navigation: Bestellungen', 
    desc: 'Bestellverwaltung im Admin-Menü anzeigen', 
    adminLevel: 'manager' 
  },
  'admin:nav:invoices': { 
    label: 'Navigation: Rechnungen', 
    desc: 'Rechnungsverwaltung im Admin-Menü anzeigen', 
    adminLevel: 'manager' 
  },
  'admin:nav:plaques': { 
    label: 'Navigation: Plaketten', 
    desc: 'Plakettenverwaltung im Admin-Menü anzeigen', 
    adminLevel: 'manager' 
  },
  'admin:nav:qrcodes': { 
    label: 'Navigation: QR-Codes', 
    desc: 'QR-Code Verwaltung im Admin-Menü anzeigen', 
    adminLevel: 'manager' 
  },

  // Accounts Management
  'accounts:view:all': { 
    label: 'Alle Konten ansehen', 
    desc: 'Alle Benutzerkonten im System einsehen (Admin)', 
    adminLevel: 'admin' 
  },
  'accounts:create': { 
    label: 'Konten erstellen', 
    desc: 'Neue Benutzerkonten anlegen (Admin)', 
    adminLevel: 'admin' 
  },
  'accounts:edit': { 
    label: 'Konten bearbeiten', 
    desc: 'Bestehende Konten ändern (Admin)', 
    adminLevel: 'admin' 
  },
  'accounts:delete': { 
    label: 'Konten löschen', 
    desc: 'Benutzerkonten entfernen (Admin)', 
    adminLevel: 'admin' 
  },

  // Activity Logs
  'activity_logs:view:all': { 
    label: 'Aktivitätslogs ansehen', 
    desc: 'Alle System-Aktivitäten einsehen (Admin)', 
    adminLevel: 'admin' 
  },
  'activity_logs:create': { 
    label: 'Aktivitätslogs erstellen', 
    desc: 'Neue Aktivitätslogs anlegen', 
    adminLevel: 'admin' 
  },
  'activity_logs:edit': { 
    label: 'Aktivitätslogs bearbeiten', 
    desc: 'Aktivitätslogs ändern (Admin)', 
    adminLevel: 'admin' 
  },
  'activity_logs:delete': { 
    label: 'Aktivitätslogs löschen', 
    desc: 'Aktivitätslogs entfernen (Admin)', 
    adminLevel: 'admin' 
  },

  // API Keys & Request Logs
  'api_keys:view:all': { 
    label: 'Alle API Keys ansehen', 
    desc: 'Alle API-Schlüssel einsehen (Admin)', 
    adminLevel: 'admin' 
  },
  'api_keys:create': { 
    label: 'API Keys erstellen', 
    desc: 'Neue API-Schlüssel generieren (Admin)', 
    adminLevel: 'admin' 
  },
  'api_keys:edit': { 
    label: 'API Keys bearbeiten', 
    desc: 'API-Schlüssel ändern (Admin)', 
    adminLevel: 'admin' 
  },
  'api_keys:delete': { 
    label: 'API Keys löschen', 
    desc: 'API-Schlüssel entfernen (Admin)', 
    adminLevel: 'admin' 
  },
  'api_request_logs:view:all': { 
    label: 'API Request Logs ansehen', 
    desc: 'Alle API-Anfragen einsehen (Admin)', 
    adminLevel: 'admin' 
  },
  'api_request_logs:create': { 
    label: 'API Request Logs erstellen', 
    desc: 'API-Anfrage-Logs erstellen', 
    adminLevel: 'admin' 
  },
  'api_request_logs:edit': { 
    label: 'API Request Logs bearbeiten', 
    desc: 'API-Anfrage-Logs ändern (Admin)', 
    adminLevel: 'admin' 
  },
  'api_request_logs:delete': { 
    label: 'API Request Logs löschen', 
    desc: 'API-Anfrage-Logs entfernen (Admin)', 
    adminLevel: 'admin' 
  },
  'apikey:create': { 
    label: 'API Key anlegen', 
    desc: 'Eigenen API-Schlüssel erstellen', 
    adminLevel: 'manager' 
  },
  'apikey:edit': { 
    label: 'API Key bearbeiten', 
    desc: 'Eigene API-Schlüssel ändern', 
    adminLevel: 'manager' 
  },
  'apikey:delete': { 
    label: 'API Key löschen', 
    desc: 'Eigene API-Schlüssel entfernen', 
    adminLevel: 'manager' 
  },
  'apikey:view': { 
    label: 'API Key ansehen', 
    desc: 'Eigene API-Schlüssel einsehen', 
    adminLevel: 'manager' 
  },
  'apikey:view:all': { 
    label: 'Alle API Keys einsehen', 
    desc: 'Alle API-Schlüssel im System ansehen (Admin)', 
    adminLevel: 'admin' 
  },

  // Bestatter Branches
  'bestatter_branches:view:all': { 
    label: 'Bestatter-Filialen ansehen', 
    desc: 'Alle Bestatter-Filialen einsehen', 
    adminLevel: 'manager' 
  },
  'bestatter_branches:create': { 
    label: 'Bestatter-Filialen erstellen', 
    desc: 'Neue Bestatter-Filialen anlegen', 
    adminLevel: 'manager' 
  },
  'bestatter_branches:edit': { 
    label: 'Bestatter-Filialen bearbeiten', 
    desc: 'Bestatter-Filialen ändern', 
    adminLevel: 'manager' 
  },
  'bestatter_branches:delete': { 
    label: 'Bestatter-Filialen löschen', 
    desc: 'Bestatter-Filialen entfernen', 
    adminLevel: 'manager' 
  },

  // Bulk Operations
  'bulk_operations:view:all': { 
    label: 'Massenoperationen ansehen', 
    desc: 'Alle Massenoperationen einsehen (Admin)', 
    adminLevel: 'admin' 
  },
  'bulk_operations:create': { 
    label: 'Massenoperationen erstellen', 
    desc: 'Neue Massenoperationen durchführen (Admin)', 
    adminLevel: 'admin' 
  },
  'bulk_operations:edit': { 
    label: 'Massenoperationen bearbeiten', 
    desc: 'Massenoperationen ändern (Admin)', 
    adminLevel: 'admin' 
  },
  'bulk_operations:delete': { 
    label: 'Massenoperationen löschen', 
    desc: 'Massenoperationen entfernen (Admin)', 
    adminLevel: 'admin' 
  },

  // Cart Items
  'cart_items:view:all': { 
    label: 'Alle Warenkorb-Artikel ansehen', 
    desc: 'Alle Artikel in Warenkörben einsehen', 
    adminLevel: 'manager' 
  },
  'cart_items:create': { 
    label: 'Warenkorb-Artikel hinzufügen', 
    desc: 'Artikel zum Warenkorb hinzufügen', 
    adminLevel: 'user' 
  },
  'cart_items:edit': { 
    label: 'Warenkorb-Artikel bearbeiten', 
    desc: 'Warenkorb-Artikel ändern', 
    adminLevel: 'user' 
  },
  'cart_items:delete': { 
    label: 'Warenkorb-Artikel löschen', 
    desc: 'Artikel aus Warenkorb entfernen', 
    adminLevel: 'user' 
  },

  // Collaboration & Invitations
  'collaboration:invite': { 
    label: 'Kollaboratoren einladen', 
    desc: 'Andere Nutzer zur Zusammenarbeit einladen', 
    adminLevel: 'user' 
  },
  'collaboration:manage': { 
    label: 'Kollaborationen verwalten', 
    desc: 'Zusammenarbeit verwalten und koordinieren', 
    adminLevel: 'manager' 
  },
  'collaboration:remove': { 
    label: 'Kollaboratoren entfernen', 
    desc: 'Nutzer aus Kollaboration entfernen', 
    adminLevel: 'manager' 
  },
  'collaboration:view': { 
    label: 'Kollaborationen ansehen', 
    desc: 'Bestehende Kollaborationen einsehen', 
    adminLevel: 'user' 
  },
  'collaboration_invitations:view:all': { 
    label: 'Alle Einladungen ansehen', 
    desc: 'Alle Kollaborations-Einladungen einsehen', 
    adminLevel: 'manager' 
  },
  'collaboration_invitations:create': { 
    label: 'Einladungen erstellen', 
    desc: 'Neue Einladungen versenden', 
    adminLevel: 'user' 
  },
  'collaboration_invitations:edit': { 
    label: 'Einladungen bearbeiten', 
    desc: 'Einladungen ändern', 
    adminLevel: 'manager' 
  },
  'collaboration_invitations:delete': { 
    label: 'Einladungen löschen', 
    desc: 'Einladungen zurückziehen', 
    adminLevel: 'manager' 
  },
  'collaborator_activity_logs:view:all': { 
    label: 'Kollaborator-Aktivitäten ansehen', 
    desc: 'Aktivitäten aller Kollaboratoren einsehen', 
    adminLevel: 'manager' 
  },
  'collaborator_activity_logs:create': { 
    label: 'Kollaborator-Aktivitäten protokollieren', 
    desc: 'Aktivitäten protokollieren', 
    adminLevel: 'user' 
  },
  'collaborator_activity_logs:edit': { 
    label: 'Kollaborator-Aktivitäten bearbeiten', 
    desc: 'Aktivitätsprotokolle ändern (Admin)', 
    adminLevel: 'admin' 
  },
  'collaborator_activity_logs:delete': { 
    label: 'Kollaborator-Aktivitäten löschen', 
    desc: 'Aktivitätsprotokolle entfernen (Admin)', 
    adminLevel: 'admin' 
  },
  'account:add_member': { 
    label: 'Mitglieder hinzufügen', 
    desc: 'Neue Mitglieder zum Konto hinzufügen', 
    adminLevel: 'manager' 
  },
  'account:remove_member': { 
    label: 'Mitglieder entfernen', 
    desc: 'Mitglieder aus Konto entfernen', 
    adminLevel: 'manager' 
  },
  'account:manage_permissions': { 
    label: 'Berechtigungen verwalten', 
    desc: 'Mitglieder-Berechtigungen verwalten', 
    adminLevel: 'manager' 
  },

  // Comments
  'comments:view:all': { 
    label: 'Alle Kommentare ansehen', 
    desc: 'Alle Kommentare im System einsehen', 
    adminLevel: 'manager' 
  },
  'comments:create': { 
    label: 'Kommentare erstellen', 
    desc: 'Neue Kommentare schreiben', 
    adminLevel: 'user' 
  },
  'comments:edit': { 
    label: 'Kommentare bearbeiten', 
    desc: 'Eigene Kommentare ändern', 
    adminLevel: 'user' 
  },
  'comments:delete': { 
    label: 'Kommentare löschen', 
    desc: 'Kommentare entfernen', 
    adminLevel: 'manager' 
  },

  // Credits & Packages
  'credit:read': { 
    label: 'Credits lesen', 
    desc: 'Credit-Stand einsehen', 
    adminLevel: 'user' 
  },
  'credit:purchase': { 
    label: 'Credits kaufen', 
    desc: 'Credit-Pakete erwerben', 
    adminLevel: 'user' 
  },
  'credits:view': { 
    label: 'Credits ansehen', 
    desc: 'Credit-Guthaben einsehen', 
    adminLevel: 'user' 
  },
  'credits:edit': { 
    label: 'Credits bearbeiten', 
    desc: 'Credit-Guthaben ändern (Admin)', 
    adminLevel: 'admin' 
  },
  'credits:delete': { 
    label: 'Credits löschen', 
    desc: 'Credit-Einträge entfernen (Admin)', 
    adminLevel: 'admin' 
  },
  'credits:manage': { 
    label: 'Credits verwalten', 
    desc: 'Credit-System verwalten (Admin)', 
    adminLevel: 'admin' 
  },
  'credit_packages:view:all': { 
    label: 'Credit-Pakete ansehen', 
    desc: 'Alle Credit-Pakete einsehen', 
    adminLevel: 'manager' 
  },
  'credit_packages:create': { 
    label: 'Credit-Pakete erstellen', 
    desc: 'Neue Credit-Pakete anlegen (Admin)', 
    adminLevel: 'admin' 
  },
  'credit_packages:edit': { 
    label: 'Credit-Pakete bearbeiten', 
    desc: 'Credit-Pakete ändern (Admin)', 
    adminLevel: 'admin' 
  },
  'credit_packages:delete': { 
    label: 'Credit-Pakete löschen', 
    desc: 'Credit-Pakete entfernen (Admin)', 
    adminLevel: 'admin' 
  },
  'credit_transactions:view:all': { 
    label: 'Credit-Transaktionen ansehen', 
    desc: 'Alle Credit-Transaktionen einsehen', 
    adminLevel: 'manager' 
  },
  'credit_transactions:create': { 
    label: 'Credit-Transaktionen erstellen', 
    desc: 'Credit-Transaktionen durchführen', 
    adminLevel: 'manager' 
  },
  'credit_transactions:edit': { 
    label: 'Credit-Transaktionen bearbeiten', 
    desc: 'Credit-Transaktionen ändern (Admin)', 
    adminLevel: 'admin' 
  },
  'credit_transactions:delete': { 
    label: 'Credit-Transaktionen löschen', 
    desc: 'Credit-Transaktionen entfernen (Admin)', 
    adminLevel: 'admin' 
  },

  // Customer Entitlements
  'customer_entitlements:view:all': { 
    label: 'Kundenberechtigungen ansehen', 
    desc: 'Alle Kundenberechtigungen einsehen', 
    adminLevel: 'manager' 
  },
  'customer_entitlements:create': { 
    label: 'Kundenberechtigungen erstellen', 
    desc: 'Neue Kundenberechtigungen anlegen', 
    adminLevel: 'manager' 
  },
  'customer_entitlements:edit': { 
    label: 'Kundenberechtigungen bearbeiten', 
    desc: 'Kundenberechtigungen ändern', 
    adminLevel: 'manager' 
  },
  'customer_entitlements:delete': { 
    label: 'Kundenberechtigungen löschen', 
    desc: 'Kundenberechtigungen entfernen', 
    adminLevel: 'manager' 
  },
  'entitlements:create': { 
    label: 'Berechtigungen erstellen', 
    desc: 'Neue Berechtigungen vergeben', 
    adminLevel: 'manager' 
  },
  'entitlements:view': { 
    label: 'Berechtigungen ansehen', 
    desc: 'Berechtigungen einsehen', 
    adminLevel: 'user' 
  },
  'entitlements:view:own': { 
    label: 'Eigene Berechtigungen ansehen', 
    desc: 'Eigene Berechtigungen einsehen', 
    adminLevel: 'user' 
  },
  'entitlements:view:all': { 
    label: 'Alle Berechtigungen ansehen', 
    desc: 'Alle Berechtigungen im System einsehen', 
    adminLevel: 'manager' 
  },
  'entitlements:edit': { 
    label: 'Berechtigungen bearbeiten', 
    desc: 'Berechtigungen ändern', 
    adminLevel: 'manager' 
  },
  'entitlements:delete': { 
    label: 'Berechtigungen löschen', 
    desc: 'Berechtigungen entfernen', 
    adminLevel: 'manager' 
  },
  'entitlements:manage': { 
    label: 'Berechtigungen verwalten', 
    desc: 'Berechtigungssystem verwalten', 
    adminLevel: 'manager' 
  },

    // Directory
  'directory:access': { 
    label: 'Directory Zugang', 
    desc: 'Zugriff auf das Directory im Dashboard (Sichtbarkeit der Seite)'
  },

  'directory:provider:view': { 
    label: 'Anbieter ansehen', 
    desc: 'Anbieter im Admin-Backend einsehen', 
    adminLevel: 'manager' 
  },
  'directory:provider:create': { 
    label: 'Anbieter anlegen', 
    desc: 'Anbieter im Admin-Backend erstellen', 
    adminLevel: 'admin' 
  },
  'directory:provider:edit': { 
    label: 'Anbieter bearbeiten', 
    desc: 'Anbieter im Admin-Backend ändern', 
    adminLevel: 'admin' 
  },
  'directory:provider:delete': { 
    label: 'Anbieter löschen', 
    desc: 'Anbieter im Admin-Backend entfernen', 
    adminLevel: 'admin' 
  },

  'directory:category:view': { 
    label: 'Kategorien ansehen', 
    desc: 'Kategorien im Admin-Backend einsehen', 
    adminLevel: 'manager' 
  },
  'directory:category:create': { 
    label: 'Kategorien anlegen', 
    desc: 'Kategorien im Admin-Backend erstellen', 
    adminLevel: 'admin' 
  },
  'directory:category:edit': { 
    label: 'Kategorien bearbeiten', 
    desc: 'Kategorien im Admin-Backend ändern', 
    adminLevel: 'admin' 
  },
  'directory:category:delete': { 
    label: 'Kategorien löschen', 
    desc: 'Kategorien im Admin-Backend entfernen', 
    adminLevel: 'admin' 
  },

  'directory:item:view': { 
    label: 'Einträge ansehen', 
    desc: 'Directory-Einträge im Admin-Backend einsehen', 
    adminLevel: 'manager' 
  },
  'directory:item:create': { 
    label: 'Einträge anlegen', 
    desc: 'Directory-Einträge im Admin-Backend erstellen', 
    adminLevel: 'admin' 
  },
  'directory:item:edit': { 
    label: 'Einträge bearbeiten', 
    desc: 'Directory-Einträge im Admin-Backend ändern', 
    adminLevel: 'admin' 
  },
  'directory:item:delete': { 
    label: 'Einträge löschen', 
    desc: 'Directory-Einträge im Admin-Backend entfernen', 
    adminLevel: 'admin' 
  },

  // Discount Codes
  'discount_codes:view:all': { 
    label: 'Rabattcodes ansehen', 
    desc: 'Alle Rabattcodes einsehen', 
    adminLevel: 'manager' 
  },
  'discount_codes:create': { 
    label: 'Rabattcodes erstellen', 
    desc: 'Neue Rabattcodes generieren', 
    adminLevel: 'manager' 
  },
  'discount_codes:edit': { 
    label: 'Rabattcodes bearbeiten', 
    desc: 'Rabattcodes ändern', 
    adminLevel: 'manager' 
  },
  'discount_codes:delete': { 
    label: 'Rabattcodes löschen', 
    desc: 'Rabattcodes entfernen', 
    adminLevel: 'manager' 
  },

  // Events
  'event:create': { 
    label: 'Events erstellen', 
    desc: 'Neue Veranstaltungen anlegen', 
    adminLevel: 'user' 
  },
  'event:create:custom': { 
    label: 'Custom Events erstellen', 
    desc: 'Benutzerdefinierte Veranstaltungen anlegen', 
    adminLevel: 'user' 
  },
  'event:view': { 
    label: 'Events ansehen', 
    desc: 'Veranstaltungen einsehen', 
    adminLevel: 'user' 
  },
  'event:edit': { 
    label: 'Events bearbeiten', 
    desc: 'Veranstaltungen ändern', 
    adminLevel: 'user' 
  },
  'event:edit:location': { 
    label: 'Event-Ort bearbeiten', 
    desc: 'Veranstaltungsort ändern', 
    adminLevel: 'manager' 
  },
  'event:delete': { 
    label: 'Events löschen', 
    desc: 'Veranstaltungen entfernen', 
    adminLevel: 'manager' 
  },
  'event:delete:custom': { 
    label: 'Custom Events löschen', 
    desc: 'Benutzerdefinierte Veranstaltungen entfernen', 
    adminLevel: 'user' 
  },
  'event:manage_rsvp': { 
    label: 'RSVPs verwalten', 
    desc: 'Teilnehmeranmeldungen verwalten', 
    adminLevel: 'manager' 
  },
  'event_comments:view:all': { 
    label: 'Event-Kommentare ansehen', 
    desc: 'Alle Event-Kommentare einsehen', 
    adminLevel: 'manager' 
  },
  'event_comments:create': { 
    label: 'Event-Kommentare erstellen', 
    desc: 'Kommentare zu Events schreiben', 
    adminLevel: 'user' 
  },
  'event_comments:edit': { 
    label: 'Event-Kommentare bearbeiten', 
    desc: 'Event-Kommentare ändern', 
    adminLevel: 'user' 
  },
  'event_comments:delete': { 
    label: 'Event-Kommentare löschen', 
    desc: 'Event-Kommentare entfernen', 
    adminLevel: 'manager' 
  },
  'event_notifications:view:all': { 
    label: 'Event-Benachrichtigungen ansehen', 
    desc: 'Alle Event-Benachrichtigungen einsehen', 
    adminLevel: 'manager' 
  },
  'event_notifications:create': { 
    label: 'Event-Benachrichtigungen erstellen', 
    desc: 'Benachrichtigungen für Events versenden', 
    adminLevel: 'manager' 
  },
  'event_notifications:edit': { 
    label: 'Event-Benachrichtigungen bearbeiten', 
    desc: 'Event-Benachrichtigungen ändern', 
    adminLevel: 'manager' 
  },
  'event_notifications:delete': { 
    label: 'Event-Benachrichtigungen löschen', 
    desc: 'Event-Benachrichtigungen entfernen', 
    adminLevel: 'manager' 
  },
  'event_rsvp:view:all': { 
    label: 'Alle RSVPs ansehen', 
    desc: 'Alle Teilnehmeranmeldungen einsehen', 
    adminLevel: 'manager' 
  },
  'event_rsvp:create': { 
    label: 'RSVP erstellen', 
    desc: 'Sich für Events anmelden', 
    adminLevel: 'user' 
  },
  'event_rsvp:edit': { 
    label: 'RSVPs bearbeiten', 
    desc: 'Teilnehmeranmeldungen ändern', 
    adminLevel: 'manager' 
  },
  'event_rsvp:delete': { 
    label: 'RSVPs löschen', 
    desc: 'Teilnehmeranmeldungen entfernen', 
    adminLevel: 'manager' 
  },

  // Family Tree
  'family_tree:create': { 
    label: 'Stammbäume erstellen', 
    desc: 'Neue Familienstammbäume anlegen', 
    adminLevel: 'user' 
  },
  'family_tree:view': { 
    label: 'Stammbäume ansehen', 
    desc: 'Familienstammbäume einsehen', 
    adminLevel: 'user' 
  },
  'family_tree:edit': { 
    label: 'Stammbäume bearbeiten', 
    desc: 'Familienstammbäume ändern', 
    adminLevel: 'user' 
  },
  'family_tree:delete': { 
    label: 'Stammbäume löschen', 
    desc: 'Familienstammbäume entfernen', 
    adminLevel: 'manager' 
  },
  'family_tree:export': { 
    label: 'Stammbäume exportieren', 
    desc: 'Stammbäume als Datei exportieren', 
    adminLevel: 'user' 
  },
  'family_tree:import': { 
    label: 'Stammbäume importieren', 
    desc: 'Stammbäume aus Datei importieren', 
    adminLevel: 'manager' 
  },
  'family_tree:manage:all': { 
    label: 'Alle Stammbäume verwalten', 
    desc: 'Alle Stammbäume im System verwalten', 
    adminLevel: 'admin' 
  },
  'family_link_requests:view:all': { 
    label: 'Familien-Verknüpfungen ansehen', 
    desc: 'Alle Familien-Verknüpfungsanfragen einsehen', 
    adminLevel: 'manager' 
  },
  'family_link_requests:create': { 
    label: 'Familien-Verknüpfung anfordern', 
    desc: 'Neue Familien-Verknüpfungen anfordern', 
    adminLevel: 'user' 
  },
  'family_link_requests:edit': { 
    label: 'Familien-Verknüpfungen bearbeiten', 
    desc: 'Verknüpfungsanfragen ändern', 
    adminLevel: 'manager' 
  },
  'family_link_requests:delete': { 
    label: 'Familien-Verknüpfungen löschen', 
    desc: 'Verknüpfungsanfragen entfernen', 
    adminLevel: 'manager' 
  },
  'family_members:view:all': { 
    label: 'Familienmitglieder ansehen', 
    desc: 'Alle Familienmitglieder einsehen', 
    adminLevel: 'manager' 
  },
  'family_members:create': { 
    label: 'Familienmitglieder hinzufügen', 
    desc: 'Neue Familienmitglieder anlegen', 
    adminLevel: 'user' 
  },
  'family_members:edit': { 
    label: 'Familienmitglieder bearbeiten', 
    desc: 'Familienmitglieder-Daten ändern', 
    adminLevel: 'user' 
  },
  'family_members:delete': { 
    label: 'Familienmitglieder entfernen', 
    desc: 'Familienmitglieder löschen', 
    adminLevel: 'manager' 
  },
  'family_relationships:view:all': { 
    label: 'Familienbeziehungen ansehen', 
    desc: 'Alle Familienbeziehungen einsehen', 
    adminLevel: 'manager' 
  },
  'family_relationships:create': { 
    label: 'Familienbeziehungen erstellen', 
    desc: 'Neue Familienbeziehungen definieren', 
    adminLevel: 'user' 
  },
  'family_relationships:edit': { 
    label: 'Familienbeziehungen bearbeiten', 
    desc: 'Familienbeziehungen ändern', 
    adminLevel: 'user' 
  },
  'family_relationships:delete': { 
    label: 'Familienbeziehungen löschen', 
    desc: 'Familienbeziehungen entfernen', 
    adminLevel: 'manager' 
  },
  'family_section_settings:view:all': { 
    label: 'Familien-Einstellungen ansehen', 
    desc: 'Alle Familien-Bereichseinstellungen einsehen', 
    adminLevel: 'manager' 
  },
  'family_section_settings:create': { 
    label: 'Familien-Einstellungen erstellen', 
    desc: 'Neue Bereichseinstellungen anlegen', 
    adminLevel: 'manager' 
  },
  'family_section_settings:edit': { 
    label: 'Familien-Einstellungen bearbeiten', 
    desc: 'Bereichseinstellungen ändern', 
    adminLevel: 'manager' 
  },
  'family_section_settings:delete': { 
    label: 'Familien-Einstellungen löschen', 
    desc: 'Bereichseinstellungen entfernen', 
    adminLevel: 'admin' 
  },

  // Invoices
  'invoice:create': { 
    label: 'Rechnungen erstellen', 
    desc: 'Neue Rechnungen ausstellen', 
    adminLevel: 'manager' 
  },
  'invoice:view': { 
    label: 'Rechnungen ansehen', 
    desc: 'Eigene Rechnungen einsehen', 
    adminLevel: 'user' 
  },
  'invoice:view:all': { 
    label: 'Alle Rechnungen ansehen', 
    desc: 'Alle Rechnungen im System einsehen', 
    adminLevel: 'manager' 
  },
  'invoice:edit': { 
    label: 'Rechnungen bearbeiten', 
    desc: 'Rechnungen ändern', 
    adminLevel: 'manager' 
  },
  'invoice:generate_sds_collective': { 
    label: 'SDS-Sammelrechnungen erstellen', 
    desc: 'Sammelrechnungen für SDS generieren (Admin)', 
    adminLevel: 'admin' 
  },
  'invoice:generate_bestatter_collective': { 
    label: 'Bestatter-Sammelrechnungen', 
    desc: 'Sammelrechnungen für Bestatter generieren', 
    adminLevel: 'manager' 
  },
  'invoice:generate_b2b': { 
    label: 'B2B-Rechnungen erstellen', 
    desc: 'Business-to-Business Rechnungen generieren', 
    adminLevel: 'manager' 
  },
  'invoice:generate_pdf': { 
    label: 'PDF-Rechnungen generieren', 
    desc: 'Rechnungen als PDF erstellen', 
    adminLevel: 'user' 
  },
  'invoice:send_email': { 
    label: 'Rechnungen per E-Mail senden', 
    desc: 'Rechnungen per E-Mail versenden', 
    adminLevel: 'manager' 
  },
  'invoices:view:all': { 
    label: 'Rechnungsübersicht', 
    desc: 'Alle Rechnungen in der Übersicht', 
    adminLevel: 'manager' 
  },
  'invoices:create': { 
    label: 'Rechnungen anlegen', 
    desc: 'Neue Rechnungen im System anlegen', 
    adminLevel: 'manager' 
  },
  'invoices:edit': { 
    label: 'Rechnungen ändern', 
    desc: 'Bestehende Rechnungen bearbeiten', 
    adminLevel: 'manager' 
  },
  'invoices:delete': { 
    label: 'Rechnungen löschen', 
    desc: 'Rechnungen aus System entfernen (Admin)', 
    adminLevel: 'admin' 
  },

  // Media
  'media:upload': { 
    label: 'Medien hochladen', 
    desc: 'Bilder und Videos hochladen', 
    adminLevel: 'user' 
  },
  'media:view': { 
    label: 'Medien ansehen', 
    desc: 'Eigene Medien einsehen', 
    adminLevel: 'user' 
  },
  'media:view:all': { 
    label: 'Alle Medien ansehen', 
    desc: 'Alle Medien im System einsehen', 
    adminLevel: 'manager' 
  },
  'media:create': { 
    label: 'Medien erstellen', 
    desc: 'Neue Medieneinträge anlegen', 
    adminLevel: 'user' 
  },
  'media:edit': { 
    label: 'Medien bearbeiten', 
    desc: 'Medien-Metadaten ändern', 
    adminLevel: 'user' 
  },
  'media:delete': { 
    label: 'Medien löschen', 
    desc: 'Medien entfernen', 
    adminLevel: 'user' 
  },
  'media:manage': { 
    label: 'Medien verwalten', 
    desc: 'Medienverwaltung durchführen', 
    adminLevel: 'manager' 
  },

  // Memberships
  'memberships:view:all': { 
    label: 'Mitgliedschaften ansehen', 
    desc: 'Alle Mitgliedschaften einsehen', 
    adminLevel: 'manager' 
  },
  'memberships:create': { 
    label: 'Mitgliedschaften erstellen', 
    desc: 'Neue Mitgliedschaften anlegen', 
    adminLevel: 'manager' 
  },
  'memberships:edit': { 
    label: 'Mitgliedschaften bearbeiten', 
    desc: 'Mitgliedschaften ändern', 
    adminLevel: 'manager' 
  },
  'memberships:delete': { 
    label: 'Mitgliedschaften löschen', 
    desc: 'Mitgliedschaften entfernen', 
    adminLevel: 'admin' 
  },

  // Memorial Albums
  'memorial_album_access:view:all': { 
    label: 'Album-Zugriffe ansehen', 
    desc: 'Alle Gedenkalbum-Zugriffe einsehen', 
    adminLevel: 'manager' 
  },
  'memorial_album_access:create': { 
    label: 'Album-Zugriffe vergeben', 
    desc: 'Zugriffsrechte für Alben vergeben', 
    adminLevel: 'manager' 
  },
  'memorial_album_access:edit': { 
    label: 'Album-Zugriffe bearbeiten', 
    desc: 'Zugriffsrechte ändern', 
    adminLevel: 'manager' 
  },
  'memorial_album_access:delete': { 
    label: 'Album-Zugriffe entziehen', 
    desc: 'Zugriffsrechte entfernen', 
    adminLevel: 'manager' 
  },
  'memorial_album_comments:view:all': { 
    label: 'Album-Kommentare ansehen', 
    desc: 'Alle Album-Kommentare einsehen', 
    adminLevel: 'manager' 
  },
  'memorial_album_comments:create': { 
    label: 'Album-Kommentare schreiben', 
    desc: 'Kommentare zu Alben verfassen', 
    adminLevel: 'user' 
  },
  'memorial_album_comments:edit': { 
    label: 'Album-Kommentare bearbeiten', 
    desc: 'Album-Kommentare ändern', 
    adminLevel: 'user' 
  },
  'memorial_album_comments:delete': { 
    label: 'Album-Kommentare löschen', 
    desc: 'Album-Kommentare entfernen', 
    adminLevel: 'manager' 
  },
  'memorial_album_downloads:view:all': { 
    label: 'Album-Downloads ansehen', 
    desc: 'Alle Album-Download-Statistiken einsehen', 
    adminLevel: 'manager' 
  },
  'memorial_album_downloads:create': { 
    label: 'Album-Downloads protokollieren', 
    desc: 'Downloads aufzeichnen', 
    adminLevel: 'user' 
  },
  'memorial_album_downloads:edit': { 
    label: 'Album-Downloads bearbeiten', 
    desc: 'Download-Statistiken ändern (Admin)', 
    adminLevel: 'admin' 
  },
  'memorial_album_downloads:delete': { 
    label: 'Album-Downloads löschen', 
    desc: 'Download-Statistiken entfernen (Admin)', 
    adminLevel: 'admin' 
  },
  'memorial_album_images:view:all': { 
    label: 'Album-Bilder ansehen', 
    desc: 'Alle Album-Bilder einsehen', 
    adminLevel: 'manager' 
  },
  'memorial_album_images:create': { 
    label: 'Album-Bilder hinzufügen', 
    desc: 'Bilder zu Alben hinzufügen', 
    adminLevel: 'user' 
  },
  'memorial_album_images:edit': { 
    label: 'Album-Bilder bearbeiten', 
    desc: 'Album-Bilder ändern', 
    adminLevel: 'user' 
  },
  'memorial_album_images:delete': { 
    label: 'Album-Bilder löschen', 
    desc: 'Bilder aus Alben entfernen', 
    adminLevel: 'user' 
  },
  'memorial_albums:view:all': { 
    label: 'Gedenkalben ansehen', 
    desc: 'Alle Gedenkalben einsehen', 
    adminLevel: 'manager' 
  },
  'memorial_albums:create': { 
    label: 'Gedenkalben erstellen', 
    desc: 'Neue Gedenkalben anlegen', 
    adminLevel: 'user' 
  },
  'memorial_albums:edit': { 
    label: 'Gedenkalben bearbeiten', 
    desc: 'Gedenkalben ändern', 
    adminLevel: 'user' 
  },
  'memorial_albums:delete': { 
    label: 'Gedenkalben löschen', 
    desc: 'Gedenkalben entfernen', 
    adminLevel: 'manager' 
  },

  // Memorial Collaborators
  'memorial:add_collaborator': { 
    label: 'Gedenkseiten-Mitarbeiter hinzufügen', 
    desc: 'Neue Mitarbeiter zu Gedenkseiten hinzufügen', 
    adminLevel: 'manager' 
  },
  'memorial:remove_collaborator': { 
    label: 'Gedenkseiten-Mitarbeiter entfernen', 
    desc: 'Mitarbeiter von Gedenkseiten entfernen', 
    adminLevel: 'manager' 
  },
  'memorial:manage_collaborators': { 
    label: 'Gedenkseiten-Mitarbeiter verwalten', 
    desc: 'Alle Mitarbeiter einer Gedenkseite verwalten', 
    adminLevel: 'manager' 
  },
  'memorial_collaborators:view:all': { 
    label: 'Gedenkseiten-Mitarbeiter ansehen', 
    desc: 'Alle Gedenkseiten-Mitarbeiter einsehen', 
    adminLevel: 'manager' 
  },
  'memorial_collaborators:create': { 
    label: 'Mitarbeiter einladen', 
    desc: 'Neue Mitarbeiter einladen', 
    adminLevel: 'manager' 
  },
  'memorial_collaborators:edit': { 
    label: 'Mitarbeiter-Rechte ändern', 
    desc: 'Rechte von Mitarbeitern anpassen', 
    adminLevel: 'manager' 
  },
  'memorial_collaborators:delete': { 
    label: 'Mitarbeiter entfernen', 
    desc: 'Mitarbeiter-Zugang entziehen', 
    adminLevel: 'manager' 
  },

  // Memorial Events
  'memorial_events:view:all': { 
    label: 'Gedenkseiten-Events ansehen', 
    desc: 'Alle Gedenkseiten-Events einsehen', 
    adminLevel: 'manager' 
  },
  'memorial_events:create': { 
    label: 'Gedenkseiten-Events erstellen', 
    desc: 'Neue Events für Gedenkseiten anlegen', 
    adminLevel: 'user' 
  },
  'memorial_events:edit': { 
    label: 'Gedenkseiten-Events bearbeiten', 
    desc: 'Gedenkseiten-Events ändern', 
    adminLevel: 'user' 
  },
  'memorial_events:delete': { 
    label: 'Gedenkseiten-Events löschen', 
    desc: 'Gedenkseiten-Events entfernen', 
    adminLevel: 'manager' 
  },

  // Memorial Pages
  'memorial_page_album_access:view:all': { 
    label: 'Seitenalbum-Zugriffe ansehen', 
    desc: 'Alle Seitenalbum-Zugriffe einsehen', 
    adminLevel: 'manager' 
  },
  'memorial_page_album_access:create': { 
    label: 'Seitenalbum-Zugriffe vergeben', 
    desc: 'Zugriffsrechte für Seitenalben vergeben', 
    adminLevel: 'manager' 
  },
  'memorial_page_album_access:edit': { 
    label: 'Seitenalbum-Zugriffe bearbeiten', 
    desc: 'Seitenalbum-Zugriffsrechte ändern', 
    adminLevel: 'manager' 
  },
  'memorial_page_album_access:delete': { 
    label: 'Seitenalbum-Zugriffe entziehen', 
    desc: 'Seitenalbum-Zugriffsrechte entfernen', 
    adminLevel: 'manager' 
  },
  'memorial_page_album_items:view:all': { 
    label: 'Seitenalbum-Inhalte ansehen', 
    desc: 'Alle Seitenalbum-Inhalte einsehen', 
    adminLevel: 'manager' 
  },
  'memorial_page_album_items:create': { 
    label: 'Seitenalbum-Inhalte hinzufügen', 
    desc: 'Inhalte zu Seitenalben hinzufügen', 
    adminLevel: 'user' 
  },
  'memorial_page_album_items:edit': { 
    label: 'Seitenalbum-Inhalte bearbeiten', 
    desc: 'Seitenalbum-Inhalte ändern', 
    adminLevel: 'user' 
  },
  'memorial_page_album_items:delete': { 
    label: 'Seitenalbum-Inhalte löschen', 
    desc: 'Seitenalbum-Inhalte entfernen', 
    adminLevel: 'manager' 
  },
  'memorial_page_albums:view:all': { 
    label: 'Gedenkseiten-Alben ansehen', 
    desc: 'Alle Gedenkseiten-Alben einsehen', 
    adminLevel: 'manager' 
  },
  'memorial_page_albums:create': { 
    label: 'Gedenkseiten-Alben erstellen', 
    desc: 'Neue Alben für Gedenkseiten anlegen', 
    adminLevel: 'user' 
  },
  'memorial_page_albums:edit': { 
    label: 'Gedenkseiten-Alben bearbeiten', 
    desc: 'Gedenkseiten-Alben ändern', 
    adminLevel: 'user' 
  },
  'memorial_page_albums:delete': { 
    label: 'Gedenkseiten-Alben löschen', 
    desc: 'Gedenkseiten-Alben entfernen', 
    adminLevel: 'manager' 
  },
  'memorial_page_videos:view:all': { 
    label: 'Gedenkseiten-Videos ansehen', 
    desc: 'Alle Gedenkseiten-Videos einsehen', 
    adminLevel: 'manager' 
  },
  'memorial_page_videos:create': { 
    label: 'Gedenkseiten-Videos hinzufügen', 
    desc: 'Videos zu Gedenkseiten hinzufügen', 
    adminLevel: 'user' 
  },
  'memorial_page_videos:edit': { 
    label: 'Gedenkseiten-Videos bearbeiten', 
    desc: 'Gedenkseiten-Videos ändern', 
    adminLevel: 'user' 
  },
  'memorial_page_videos:delete': { 
    label: 'Gedenkseiten-Videos löschen', 
    desc: 'Gedenkseiten-Videos entfernen', 
    adminLevel: 'manager' 
  },
  'memorial_pages:view:all': { 
    label: 'Alle Gedenkseiten ansehen', 
    desc: 'Alle Gedenkseiten im System einsehen', 
    adminLevel: 'manager' 
  },
  'memorial_pages:create': { 
    label: 'Gedenkseiten erstellen', 
    desc: 'Neue Gedenkseiten anlegen', 
    adminLevel: 'user' 
  },
  'memorial_pages:edit': { 
    label: 'Gedenkseiten bearbeiten', 
    desc: 'Gedenkseiten-Inhalte ändern', 
    adminLevel: 'user' 
  },
  'memorial_pages:delete': { 
    label: 'Gedenkseiten löschen', 
    desc: 'Gedenkseiten entfernen (Admin)', 
    adminLevel: 'admin' 
  },

  // Memorial Visitor Uploads
  'memorial_visitor_uploads:view:all': { 
    label: 'Besucher-Uploads ansehen', 
    desc: 'Alle Besucher-Uploads einsehen', 
    adminLevel: 'manager' 
  },
  'memorial_visitor_uploads:create': { 
    label: 'Besucher-Uploads hochladen', 
    desc: 'Als Besucher Inhalte hochladen', 
    adminLevel: 'user' 
  },
  'memorial_visitor_uploads:moderate': { 
    label: 'Besucher-Uploads moderieren', 
    desc: 'Besucher-Uploads prüfen und freigeben', 
    adminLevel: 'manager' 
  },
  'memorial_visitor_uploads:delete': { 
    label: 'Besucher-Uploads löschen', 
    desc: 'Besucher-Uploads entfernen', 
    adminLevel: 'manager' 
  },

  // Memorial Core
  'memorial:create': { 
    label: 'Gedenkseiten anlegen', 
    desc: 'Neue Gedenkseiten erstellen', 
    adminLevel: 'user' 
  },
  'memorial:view': { 
    label: 'Gedenkseiten ansehen', 
    desc: 'Eigene Gedenkseiten einsehen', 
    adminLevel: 'user' 
  },
  'memorial:view:all': { 
    label: 'Alle Gedenkseiten ansehen', 
    desc: 'Alle Gedenkseiten im System einsehen', 
    adminLevel: 'manager' 
  },
  'memorial:edit': { 
    label: 'Gedenkseiten bearbeiten', 
    desc: 'Eigene Gedenkseiten ändern', 
    adminLevel: 'user' 
  },
  'memorial:edit:all': { 
    label: 'Alle Gedenkseiten bearbeiten', 
    desc: 'Alle Gedenkseiten im System bearbeiten (Admin)', 
    adminLevel: 'admin' 
  },
  'memorial:delete': { 
    label: 'Gedenkseiten löschen', 
    desc: 'Eigene Gedenkseiten entfernen', 
    adminLevel: 'manager' 
  },
  'memorial:delete:all': { 
    label: 'Alle Gedenkseiten löschen', 
    desc: 'Alle Gedenkseiten im System löschen (Admin)', 
    adminLevel: 'admin' 
  },
  'memorial:reassign': { 
    label: 'Gedenkseiten übertragen', 
    desc: 'Gedenkseiten anderen Nutzern zuweisen', 
    adminLevel: 'manager' 
  },
  'memorial:reassign:all': { 
    label: 'Alle Gedenkseiten übertragen', 
    desc: 'Alle Gedenkseiten neu zuweisen (Admin)', 
    adminLevel: 'admin' 
  },
  'memorial:extend': { 
    label: 'Gedenkseiten verlängern', 
    desc: 'Laufzeit eigener Gedenkseiten verlängern', 
    adminLevel: 'user' 
  },
  'memorial:extend:manage': { 
    label: 'Verlängerungen verwalten', 
    desc: 'Gedenkseiten-Verlängerungen verwalten (Admin)', 
    adminLevel: 'admin' 
  },
  'memorial:view:expired': { 
    label: 'Abgelaufene Gedenkseiten ansehen', 
    desc: 'Abgelaufene Gedenkseiten einsehen (Admin)', 
    adminLevel: 'admin' 
  },
  'memorial:edit:time_restricted': { 
    label: 'Zeitbeschränkte Bearbeitung', 
    desc: 'Gedenkseiten mit Zeitbeschränkung bearbeiten', 
    adminLevel: 'manager' 
  },

  // Memorial Groups
  'memorial_group:create': { 
    label: 'Gedenkgruppen erstellen', 
    desc: 'Neue Gedenkgruppen anlegen', 
    adminLevel: 'user' 
  },
  'memorial_group:view': { 
    label: 'Gedenkgruppen ansehen', 
    desc: 'Eigene Gedenkgruppen einsehen', 
    adminLevel: 'user' 
  },
  'memorial_group:view:all': { 
    label: 'Alle Gedenkgruppen ansehen', 
    desc: 'Alle Gedenkgruppen im System einsehen', 
    adminLevel: 'manager' 
  },
  'memorial_group:edit': { 
    label: 'Gedenkgruppen bearbeiten', 
    desc: 'Eigene Gedenkgruppen ändern', 
    adminLevel: 'user' 
  },
  'memorial_group:delete': { 
    label: 'Gedenkgruppen löschen', 
    desc: 'Gedenkgruppen entfernen', 
    adminLevel: 'manager' 
  },
  'memorial_group:add_member': { 
    label: 'Gruppenmitglieder hinzufügen', 
    desc: 'Mitglieder zu Gedenkgruppen hinzufügen', 
    adminLevel: 'user' 
  },
  'memorial_group:remove_member': { 
    label: 'Gruppenmitglieder entfernen', 
    desc: 'Mitglieder aus Gedenkgruppen entfernen', 
    adminLevel: 'manager' 
  },
  'memorial_group:manage_permissions': { 
    label: 'Gruppenrechte verwalten', 
    desc: 'Berechtigungen für Gedenkgruppen verwalten', 
    adminLevel: 'manager' 
  },

  // Memory Lights
  'memory_light:create': { 
    label: 'Gedenklichter anzünden', 
    desc: 'Neue Gedenklichter erstellen', 
    adminLevel: 'user' 
  },
  'memory_light:create:own': { 
    label: 'Eigene Gedenklichter anzünden', 
    desc: 'Gedenklichter für eigene Gedenkseiten', 
    adminLevel: 'user' 
  },
  'memory_light:create:sds': { 
    label: 'SDS-Gedenklichter anzünden', 
    desc: 'Gedenklichter für SDS-Kunden', 
    adminLevel: 'manager' 
  },
  'memory_light:create:unlimited': { 
    label: 'Unbegrenzte Gedenklichter', 
    desc: 'Unbegrenzt Gedenklichter anzünden (Admin)', 
    adminLevel: 'admin' 
  },
  'memory_light:upload_media': { 
    label: 'Gedenklicht-Medien hochladen', 
    desc: 'Bilder zu Gedenklichtern hinzufügen', 
    adminLevel: 'user' 
  },
  'memory_light:approve': { 
    label: 'Gedenklichter freigeben', 
    desc: 'Gedenklichter prüfen und freigeben', 
    adminLevel: 'manager' 
  },
  'memory_light:moderate': { 
    label: 'Gedenklichter moderieren', 
    desc: 'Gedenklichter-Inhalte moderieren', 
    adminLevel: 'manager' 
  },
  'memory_light:delete': { 
    label: 'Gedenklichter löschen', 
    desc: 'Eigene Gedenklichter entfernen', 
    adminLevel: 'user' 
  },
  'memory_light:view_unapproved': { 
    label: 'Ungeprüfte Gedenklichter ansehen', 
    desc: 'Nicht freigegebene Gedenklichter einsehen', 
    adminLevel: 'manager' 
  },
  'memory_light:view:all': { 
    label: 'Alle Gedenklichter ansehen', 
    desc: 'Alle Gedenklichter im System einsehen', 
    adminLevel: 'manager' 
  },
  'memory_light:edit:all': { 
    label: 'Alle Gedenklichter bearbeiten', 
    desc: 'Alle Gedenklichter im System ändern (Admin)', 
    adminLevel: 'admin' 
  },
  'memory_light:delete:all': { 
    label: 'Alle Gedenklichter löschen', 
    desc: 'Alle Gedenklichter im System entfernen (Admin)', 
    adminLevel: 'admin' 
  },
  'memory_lights:manage': { 
    label: 'Gedenklichter verwalten', 
    desc: 'Gedenklichter-System verwalten', 
    adminLevel: 'manager' 
  },
  'memory_lights:moderate': { 
    label: 'Gedenklichter-Moderation', 
    desc: 'Gedenklicht-Inhalte moderieren', 
    adminLevel: 'manager' 
  },
  'memory_lights:delete': { 
    label: 'Gedenklichter entfernen', 
    desc: 'Gedenklichter löschen', 
    adminLevel: 'manager' 
  },
  'memory_lights:view': { 
    label: 'Gedenklichter einsehen', 
    desc: 'Gedenklichter ansehen', 
    adminLevel: 'user' 
  },
  'memory_lights:create': { 
    label: 'Gedenklichter erstellen', 
    desc: 'Neue Gedenklichter anlegen', 
    adminLevel: 'user' 
  },

  // Gallery
  'gallery:create_album': { 
    label: 'Galerie-Alben erstellen', 
    desc: 'Neue Foto-Alben anlegen', 
    adminLevel: 'user' 
  },
  'gallery:view_private': { 
    label: 'Private Galerien ansehen', 
    desc: 'Private Galerien einsehen', 
    adminLevel: 'manager' 
  },
  'gallery:manage_album': { 
    label: 'Alben verwalten', 
    desc: 'Eigene Alben verwalten', 
    adminLevel: 'user' 
  },
  'gallery:delete_album': { 
    label: 'Alben löschen', 
    desc: 'Foto-Alben entfernen', 
    adminLevel: 'manager' 
  },
  'gallery:set_password': { 
    label: 'Album-Passwörter setzen', 
    desc: 'Passwortschutz für Alben einrichten', 
    adminLevel: 'user' 
  },
  'gallery:upload': { 
    label: 'Bilder hochladen', 
    desc: 'Bilder in Galerien hochladen', 
    adminLevel: 'user' 
  },
  'gallery:download': { 
    label: 'Bilder herunterladen', 
    desc: 'Bilder aus Galerien herunterladen', 
    adminLevel: 'user' 
  },
  'gallery:manage:all': { 
    label: 'Alle Galerien verwalten', 
    desc: 'Alle Galerien im System verwalten (Admin)', 
    adminLevel: 'admin' 
  },

  // Plaque
  'plaque:create': { 
    label: 'Plaketten bestellen', 
    desc: 'Neue Plaketten-Bestellungen aufgeben', 
    adminLevel: 'user' 
  },
  'plaque:view': { 
    label: 'Plaketten ansehen', 
    desc: 'Eigene Plaketten-Bestellungen einsehen', 
    adminLevel: 'user' 
  },
  'plaque:edit': { 
    label: 'Plaketten bearbeiten', 
    desc: 'Plaketten-Bestellungen ändern', 
    adminLevel: 'manager' 
  },
  'plaque:delete': { 
    label: 'Plaketten löschen', 
    desc: 'Plaketten-Bestellungen stornieren', 
    adminLevel: 'manager' 
  },
  'plaque:view:all': { 
    label: 'Alle Plaketten ansehen', 
    desc: 'Alle Plaketten-Bestellungen einsehen', 
    adminLevel: 'manager' 
  },

  // Laser Orders (SDS)
  'laser:order:create': {
    label: 'Laser-Bestellungen aufgeben',
    desc: 'Neue Laser-Produkt-Bestellungen über die SDS API aufgeben',
    adminLevel: 'user'
  },
  'laser:order:view': {
    label: 'Laser-Bestellungen ansehen',
    desc: 'Laser-Produkt-Bestellungen über die SDS API einsehen',
    adminLevel: 'user'
  },
  'laser:order:edit': {
    label: 'Laser-Bestellungen bearbeiten',
    desc: 'Laser-Produkt-Bestellungen über die SDS API aktualisieren',
    adminLevel: 'manager'
  },
 'laser:order:ship': {
  label: 'Laser Bestellung versenden',
  desc: 'Erlaubt das Versenden von Laser-Bestellungen an Produktion oder Versand',
  adminLevel: 'admin'
},


  // Roles
  'roles:view:all': { 
    label: 'Alle Rollen ansehen', 
    desc: 'Alle Benutzerrollen einsehen', 
    adminLevel: 'admin' 
  },
  'roles:manage': { 
    label: 'Rollen verwalten', 
    desc: 'Benutzerrollen verwalten (Admin)', 
    adminLevel: 'admin' 
  },
  'system_roles:manage': { 
    label: 'Systemrollen verwalten', 
    desc: 'System-Standardrollen verwalten (Admin)', 
    adminLevel: 'admin' 
  },

  // Shipping Rates
  'shipping_rates:manage': { 
    label: 'Versandkosten verwalten', 
    desc: 'Versandkosten-System verwalten', 
    adminLevel: 'admin' 
  },
  'shipping_rates:create': { 
    label: 'Versandkosten anlegen', 
    desc: 'Neue Versandkosten definieren', 
    adminLevel: 'admin' 
  },
  'shipping_rates:edit': { 
    label: 'Versandkosten bearbeiten', 
    desc: 'Versandkosten ändern', 
    adminLevel: 'admin' 
  },
  'shipping_rates:delete': { 
    label: 'Versandkosten löschen', 
    desc: 'Versandkosten entfernen', 
    adminLevel: 'admin' 
  },

  // Subscriptions
  'subscriptions:view': { 
    label: 'Abonnements ansehen', 
    desc: 'Eigene Abonnements einsehen', 
    adminLevel: 'user' 
  },
  'subscriptions:create': { 
    label: 'Abonnements abschließen', 
    desc: 'Neue Abonnements abschließen', 
    adminLevel: 'user' 
  },
  'subscriptions:edit': { 
    label: 'Abonnements bearbeiten', 
    desc: 'Abonnements ändern', 
    adminLevel: 'manager' 
  },
  'subscriptions:delete': { 
    label: 'Abonnements kündigen', 
    desc: 'Abonnements beenden', 
    adminLevel: 'manager' 
  },

  // User Management
  'user:create': { 
    label: 'Benutzer anlegen', 
    desc: 'Neue Benutzer erstellen', 
    adminLevel: 'manager' 
  },
  'user:view': { 
    label: 'Benutzer ansehen', 
    desc: 'Benutzerdaten einsehen', 
    adminLevel: 'manager' 
  },
  'user:view:all': { 
    label: 'Alle Benutzer ansehen', 
    desc: 'Alle Benutzer im System einsehen', 
    adminLevel: 'admin' 
  },
  'user:edit': { 
    label: 'Benutzer bearbeiten', 
    desc: 'Benutzerdaten ändern', 
    adminLevel: 'manager' 
  },
  'user:delete': { 
    label: 'Benutzer löschen', 
    desc: 'Benutzer aus System entfernen', 
    adminLevel: 'admin' 
  },
  'user:check_email': { 
    label: 'E-Mail prüfen', 
    desc: 'E-Mail-Verfügbarkeit prüfen', 
    adminLevel: 'user' 
  },

  // Branch Management
  'branch:create': { 
    label: 'Filialen erstellen', 
    desc: 'Neue Filialen anlegen', 
    adminLevel: 'manager' 
  },
  'branch:view': { 
    label: 'Filialen ansehen', 
    desc: 'Eigene Filialdaten einsehen', 
    adminLevel: 'manager' 
  },
  'branch:view:all': { 
    label: 'Alle Filialen ansehen', 
    desc: 'Alle Filialen im System einsehen', 
    adminLevel: 'admin' 
  },
  'branch:edit': { 
    label: 'Filialen bearbeiten', 
    desc: 'Filialdaten ändern', 
    adminLevel: 'manager' 
  },
  'branch:delete': { 
    label: 'Filialen löschen', 
    desc: 'Filialen entfernen', 
    adminLevel: 'admin' 
  },

  // Orders
  'order:create': { 
    label: 'Bestellungen aufgeben', 
    desc: 'Neue Bestellungen erstellen', 
    adminLevel: 'user' 
  },
  'order:view': { 
    label: 'Bestellungen ansehen', 
    desc: 'Eigene Bestellungen einsehen', 
    adminLevel: 'user' 
  },
  'order:view:all': { 
    label: 'Alle Bestellungen ansehen', 
    desc: 'Alle Bestellungen im System einsehen', 
    adminLevel: 'manager' 
  },
  'order:edit': { 
    label: 'Bestellungen bearbeiten', 
    desc: 'Bestellungen ändern', 
    adminLevel: 'manager' 
  },
  'order:delete': { 
    label: 'Bestellungen löschen', 
    desc: 'Bestellungen stornieren', 
    adminLevel: 'admin' 
  },

  // Products
  'product:create': { 
    label: 'Produkte anlegen', 
    desc: 'Neue Produkte erstellen', 
    adminLevel: 'admin' 
  },
  'product:view': { 
    label: 'Produkte ansehen', 
    desc: 'Produkte einsehen', 
    adminLevel: 'user' 
  },
  'product:view:all': { 
    label: 'Alle Produkte ansehen', 
    desc: 'Alle Produkte im System einsehen', 
    adminLevel: 'manager' 
  },
  'product:edit': { 
    label: 'Produkte bearbeiten', 
    desc: 'Produktdaten ändern', 
    adminLevel: 'admin' 
  },
  'product:delete': { 
    label: 'Produkte löschen', 
    desc: 'Produkte aus System entfernen', 
    adminLevel: 'admin' 
  },

  // Shop
  'shop:access': { 
    label: 'Shop-Zugang', 
    desc: 'Zugriff auf den Online-Shop', 
    adminLevel: 'user' 
  },
  'shop:cart:manage': { 
    label: 'Warenkorb verwalten', 
    desc: 'Eigenen Warenkorb verwalten', 
    adminLevel: 'user' 
  },
  'shop:purchase': { 
    label: 'Käufe tätigen', 
    desc: 'Produkte kaufen können', 
    adminLevel: 'user' 
  },
  'shop:checkout': { 
    label: 'Checkout durchführen', 
    desc: 'Bestellungen abschließen', 
    adminLevel: 'user' 
  },

  // Embed
  'embed:generate': { 
    label: 'Embed-Codes generieren', 
    desc: 'Einbettungscodes für Websites erstellen', 
    adminLevel: 'manager' 
  },

  // Reports
  'view_reports': { 
    label: 'Berichte ansehen', 
    desc: 'System-Berichte und Statistiken einsehen', 
    adminLevel: 'manager' 
  },

  // E-Mail
  'email:plaque_status_send': { 
    label: 'Plaketten-Status E-Mails', 
    desc: 'Status-E-Mails für Plaketten versenden', 
    adminLevel: 'manager' 
  },
  'email:plaque_order_admin_notification': { 
    label: 'Plaketten-Admin-Benachrichtigung', 
    desc: 'Admin-Benachrichtigungen für Plaketten-Bestellungen', 
    adminLevel: 'admin' 
  },
  'email:password_send': { 
    label: 'Passwort-E-Mails senden', 
    desc: 'Passwort-Reset E-Mails versenden', 
    adminLevel: 'user' 
  },

  // Moderation
  'moderation:view_queue': { 
    label: 'Moderations-Warteschlange', 
    desc: 'Zu moderierende Inhalte einsehen', 
    adminLevel: 'manager' 
  },
  'moderation:approve': { 
    label: 'Inhalte freigeben', 
    desc: 'Inhalte prüfen und freigeben', 
    adminLevel: 'manager' 
  },
  'moderation:reject': { 
    label: 'Inhalte ablehnen', 
    desc: 'Inhalte ablehnen und blockieren', 
    adminLevel: 'manager' 
  },
  'moderation:manage:all': { 
    label: 'Moderation verwalten', 
    desc: 'Gesamte Moderation verwalten (Admin)', 
    adminLevel: 'admin' 
  },

  // AI
  'ai:memorial-suggestion': { 
    label: 'KI Nachruf-Vorschläge', 
    desc: 'KI-gestützte Nachruf-Vorschläge generieren', 
    adminLevel: 'user' 
  },

  // Auth
  'auth:login': { 
    label: 'Anmeldung', 
    desc: 'System-Anmeldung durchführen', 
    adminLevel: 'user' 
  },
  'auth:register': { 
    label: 'Registrierung', 
    desc: 'Neues Konto registrieren', 
    adminLevel: 'user' 
  },
  'auth:login:google': { 
    label: 'Google-Anmeldung', 
    desc: 'Mit Google-Konto anmelden', 
    adminLevel: 'user' 
  },
  'auth:login:facebook': { 
    label: 'Facebook-Anmeldung', 
    desc: 'Mit Facebook-Konto anmelden', 
    adminLevel: 'user' 
  },

  // Impersonation
  'user:impersonate': { 
    label: 'Als User einloggen', 
    desc: 'Admin kann sich als anderer User einloggen um dessen Ansicht zu sehen', 
    adminLevel: 'admin' 
  },

  // SDS Specific Permissions
  'sds:user': { 
    label: 'SDS Benutzer (Legacy)', 
    desc: 'SDS Benutzerverwaltung Vollzugriff (Alte Version)', 
    adminLevel: 'admin' 
  },
  'sds:user:view': { 
    label: 'SDS Benutzer ansehen', 
    desc: 'SDS Benutzerdaten einsehen', 
    adminLevel: 'manager' 
  },
  'sds:user:edit': { 
    label: 'SDS Benutzer bearbeiten', 
    desc: 'SDS Benutzerdaten ändern', 
    adminLevel: 'manager' 
  },
  'sds:user:delete': { 
    label: 'SDS Benutzer löschen', 
    desc: 'SDS Benutzer entfernen', 
    adminLevel: 'admin' 
  },
  'sds:user:create': { 
    label: 'SDS Benutzer anlegen', 
    desc: 'Neue SDS Benutzer erstellen', 
    adminLevel: 'manager' 
  },
  'sds:branch': { 
    label: 'SDS Filialen (Legacy)', 
    desc: 'SDS Filialverwaltung Vollzugriff (Alte Version)', 
    adminLevel: 'admin' 
  },
  'sds:branch:view': { 
    label: 'SDS Filialen ansehen', 
    desc: 'SDS Filialdaten einsehen', 
    adminLevel: 'manager' 
  },
  'sds:branch:edit': { 
    label: 'SDS Filialen bearbeiten', 
    desc: 'SDS Filialdaten ändern', 
    adminLevel: 'manager' 
  },
  'sds:branch:delete': { 
    label: 'SDS Filialen löschen', 
    desc: 'SDS Filialen entfernen', 
    adminLevel: 'admin' 
  },
  'sds:branch:create': { 
    label: 'SDS Filialen anlegen', 
    desc: 'Neue SDS Filialen erstellen', 
    adminLevel: 'manager' 
  },
  'sds:bestatter:view': { 
    label: 'SDS Bestatter ansehen', 
    desc: 'SDS Bestatterdaten einsehen', 
    adminLevel: 'manager' 
  },
  'sds:bestatter:edit': { 
    label: 'SDS Bestatter bearbeiten', 
    desc: 'SDS Bestatterdaten ändern', 
    adminLevel: 'manager' 
  },
  'sds:bestatter:delete': { 
    label: 'SDS Bestatter löschen', 
    desc: 'SDS Bestatter entfernen', 
    adminLevel: 'admin' 
  },
  'sds:bestatter:create': { 
    label: 'SDS Bestatter anlegen', 
    desc: 'Neue SDS Bestatter erstellen', 
    adminLevel: 'manager' 
  },
  'sds:bestatter:read': { 
    label: 'SDS Bestatter lesen (Legacy)', 
    desc: 'SDS Bestatterdaten lesen (Alte Version)', 
    adminLevel: 'manager' 
  },
  'sds:bestatter:update': { 
    label: 'SDS Bestatter aktualisieren (Legacy)', 
    desc: 'SDS Bestatterdaten aktualisieren (Alte Version)', 
    adminLevel: 'manager' 
  },
  'sds:invoice': { 
    label: 'SDS Rechnungen (Legacy)', 
    desc: 'SDS Rechnungsverwaltung Vollzugriff (Alte Version)', 
    adminLevel: 'admin' 
  },
  'sds:invoice:view': { 
    label: 'SDS Rechnungen ansehen', 
    desc: 'SDS Rechnungen einsehen', 
    adminLevel: 'manager' 
  },
  'sds:invoice:edit': { 
    label: 'SDS Rechnungen bearbeiten', 
    desc: 'SDS Rechnungen ändern', 
    adminLevel: 'manager' 
  },
  'sds:invoice:create': { 
    label: 'SDS Rechnungen erstellen', 
    desc: 'Neue SDS Rechnungen ausstellen', 
    adminLevel: 'manager' 
  },
  'sds:customer': { 
    label: 'SDS Kunden (Legacy)', 
    desc: 'SDS Kundenverwaltung Vollzugriff (Alte Version)', 
    adminLevel: 'admin' 
  },
  'sds:customer:view': { 
    label: 'SDS Kunden ansehen', 
    desc: 'SDS Kundendaten einsehen', 
    adminLevel: 'manager' 
  },
  'sds:customer:edit': { 
    label: 'SDS Kunden bearbeiten', 
    desc: 'SDS Kundendaten ändern', 
    adminLevel: 'manager' 
  },
  'sds:customer:create': { 
    label: 'SDS Kunden anlegen', 
    desc: 'Neue SDS Kunden erstellen', 
    adminLevel: 'manager' 
  },
  'sds:customer:delete': { 
    label: 'SDS Kunden löschen', 
    desc: 'SDS Kunden entfernen', 
    adminLevel: 'admin' 
  },
  'sds:stats': { 
    label: 'SDS Statistiken (Legacy)', 
    desc: 'SDS Statistiken Vollzugriff (Alte Version)', 
    adminLevel: 'manager' 
  },
  'sds:stats:view': { 
    label: 'SDS Statistiken ansehen', 
    desc: 'SDS Statistiken und Auswertungen einsehen', 
    adminLevel: 'manager' 
  },
  'sds:document': { 
    label: 'SDS Dokumente (Legacy)', 
    desc: 'SDS Dokumentverwaltung Vollzugriff (Alte Version)', 
    adminLevel: 'admin' 
  },
  'sds:document:view': { 
    label: 'SDS Dokumente ansehen', 
    desc: 'SDS Dokumente einsehen', 
    adminLevel: 'manager' 
  },
  'sds:document:create': { 
    label: 'SDS Dokumente hochladen', 
    desc: 'Neue SDS Dokumente hochladen', 
    adminLevel: 'manager' 
  },
  'sds:document:edit': { 
    label: 'SDS Dokumente bearbeiten', 
    desc: 'SDS Dokumente ändern', 
    adminLevel: 'manager' 
  },
  'sds:document:delete': { 
    label: 'SDS Dokumente löschen', 
    desc: 'SDS Dokumente entfernen', 
    adminLevel: 'admin' 
  },
  'sds:case': { 
    label: 'SDS Sterbefälle (Legacy)', 
    desc: 'SDS Sterbefall-Verwaltung Vollzugriff (Alte Version)', 
    adminLevel: 'admin' 
  },
  'sds:case:view': { 
    label: 'SDS Sterbefälle ansehen', 
    desc: 'SDS Sterbefälle einsehen', 
    adminLevel: 'manager' 
  },
  'sds:case:edit': { 
    label: 'SDS Sterbefälle bearbeiten', 
    desc: 'SDS Sterbefälle ändern', 
    adminLevel: 'manager' 
  },
  'sds:case:create': { 
    label: 'SDS Sterbefälle anlegen', 
    desc: 'Neue SDS Sterbefälle erfassen', 
    adminLevel: 'manager' 
  },
  'sds:case:delete': { 
    label: 'SDS Sterbefälle löschen', 
    desc: 'SDS Sterbefälle entfernen', 
    adminLevel: 'admin' 
  },
  'sds:memorial': { 
    label: 'SDS Gedenkseiten (Legacy)', 
    desc: 'SDS Gedenkseiten-Verwaltung Vollzugriff (Alte Version)', 
    adminLevel: 'admin' 
  },
  'sds:memorial:view': { 
    label: 'SDS Gedenkseiten ansehen', 
    desc: 'SDS Gedenkseiten einsehen', 
    adminLevel: 'manager' 
  },
  'sds:memorial:edit': { 
    label: 'SDS Gedenkseiten bearbeiten', 
    desc: 'SDS Gedenkseiten ändern', 
    adminLevel: 'manager' 
  },
  'sds:memorial:create': { 
    label: 'SDS Gedenkseiten anlegen', 
    desc: 'Neue SDS Gedenkseiten erstellen', 
    adminLevel: 'manager' 
  },
  'sds:memorial:delete': { 
    label: 'SDS Gedenkseiten löschen', 
    desc: 'SDS Gedenkseiten entfernen', 
    adminLevel: 'admin' 
  },
  'sds:bulk_invoices:view': { 
    label: 'SDS Sammelrechnungen ansehen', 
    desc: 'SDS Sammelrechnungen einsehen', 
    adminLevel: 'manager' 
  },
  'sds:memory_light:view': { 
    label: 'SDS Gedenklichter ansehen', 
    desc: 'SDS Gedenklichter einsehen', 
    adminLevel: 'manager' 
  },
  'sds:memory_light:create': { 
    label: 'SDS Gedenklichter erstellen', 
    desc: 'Neue SDS Gedenklichter anlegen', 
    adminLevel: 'manager' 
  },
  'sds:memory_light:edit': { 
    label: 'SDS Gedenklichter bearbeiten', 
    desc: 'SDS Gedenklichter ändern', 
    adminLevel: 'manager' 
  },
  'sds:memory_light:delete': { 
    label: 'SDS Gedenklichter löschen', 
    desc: 'SDS Gedenklichter entfernen', 
    adminLevel: 'admin' 
  },
  'sds:event:view': { 
    label: 'SDS Events ansehen', 
    desc: 'SDS Veranstaltungen einsehen', 
    adminLevel: 'manager' 
  },
  'sds:event:create': { 
    label: 'SDS Events erstellen', 
    desc: 'Neue SDS Veranstaltungen anlegen', 
    adminLevel: 'manager' 
  },
  'sds:event:edit': { 
    label: 'SDS Events bearbeiten', 
    desc: 'SDS Veranstaltungen ändern', 
    adminLevel: 'manager' 
  },
  'sds:event:delete': { 
    label: 'SDS Events löschen', 
    desc: 'SDS Veranstaltungen entfernen', 
    adminLevel: 'admin' 
  },
  'sds:gallery:view': { 
    label: 'SDS Galerien ansehen', 
    desc: 'SDS Galerien einsehen', 
    adminLevel: 'manager' 
  },
  'sds:gallery:create_album': { 
    label: 'SDS Alben erstellen', 
    desc: 'Neue SDS Foto-Alben anlegen', 
    adminLevel: 'manager' 
  },
  'sds:gallery:manage_album': { 
    label: 'SDS Alben verwalten', 
    desc: 'SDS Foto-Alben verwalten', 
    adminLevel: 'manager' 
  },
  'sds:gallery:delete_album': { 
    label: 'SDS Alben löschen', 
    desc: 'SDS Foto-Alben entfernen', 
    adminLevel: 'admin' 
  },
  'sds:gallery:upload': { 
    label: 'SDS Bilder hochladen', 
    desc: 'Bilder in SDS Galerien hochladen', 
    adminLevel: 'manager' 
  },
  'sds:gallery:download': { 
    label: 'SDS Bilder herunterladen', 
    desc: 'Bilder aus SDS Galerien herunterladen', 
    adminLevel: 'manager' 
  },
  'sds:gallery:set_password': { 
    label: 'SDS Album-Passwörter', 
    desc: 'Passwörter für SDS Alben setzen', 
    adminLevel: 'manager' 
  },
  'sds:family_tree:view': { 
    label: 'SDS Stammbäume ansehen', 
    desc: 'SDS Familienstammbäume einsehen', 
    adminLevel: 'manager' 
  },
  'sds:family_tree:create': { 
    label: 'SDS Stammbäume erstellen', 
    desc: 'Neue SDS Stammbäume anlegen', 
    adminLevel: 'manager' 
  },
  'sds:family_tree:edit': { 
    label: 'SDS Stammbäume bearbeiten', 
    desc: 'SDS Stammbäume ändern', 
    adminLevel: 'manager' 
  },
  'sds:family_tree:delete': { 
    label: 'SDS Stammbäume löschen', 
    desc: 'SDS Stammbäume entfernen', 
    adminLevel: 'admin' 
  },
  'sds:family_tree:export': { 
    label: 'SDS Stammbäume exportieren', 
    desc: 'SDS Stammbäume als Datei exportieren', 
    adminLevel: 'manager' 
  },
  'sds:family_tree:import': { 
    label: 'SDS Stammbäume importieren', 
    desc: 'SDS Stammbäume aus Datei importieren', 
    adminLevel: 'manager' 
  },
  'sds:collaboration:view': { 
    label: 'SDS Kollaborationen ansehen', 
    desc: 'SDS Zusammenarbeit einsehen', 
    adminLevel: 'manager' 
  },
  'sds:collaboration:invite': { 
    label: 'SDS Kollaboratoren einladen', 
    desc: 'Neue SDS Kollaboratoren einladen', 
    adminLevel: 'manager' 
  },
  'sds:collaboration:manage': { 
    label: 'SDS Kollaborationen verwalten', 
    desc: 'SDS Zusammenarbeit verwalten', 
    adminLevel: 'manager' 
  },
  'sds:collaboration:remove': { 
    label: 'SDS Kollaboratoren entfernen', 
    desc: 'SDS Kollaboratoren entfernen', 
    adminLevel: 'manager' 
  },
  'sds:employee:view': { 
    label: 'SDS Mitarbeiter ansehen', 
    desc: 'SDS Mitarbeiterdaten einsehen', 
    adminLevel: 'manager' 
  },
  'sds:employee:create': { 
    label: 'SDS Mitarbeiter anlegen', 
    desc: 'Neue SDS Mitarbeiter erstellen', 
    adminLevel: 'manager' 
  },
  'sds:employee:edit': { 
    label: 'SDS Mitarbeiter bearbeiten', 
    desc: 'SDS Mitarbeiterdaten ändern', 
    adminLevel: 'manager' 
  },
  'sds:employee:delete': { 
    label: 'SDS Mitarbeiter löschen', 
    desc: 'SDS Mitarbeiter entfernen', 
    adminLevel: 'admin' 
  },
    'sds:admin': { 
    label: 'SDS Administrator', 
    desc: 'Vollständige SDS-Admin-Berechtigung', 
    adminLevel: 'admin' 
  },
  'sds:export:exclusive': { 
    label: 'SDS Exklusiv-Export', 
    desc: 'Ausschließlich SDS-Rechnungen exportieren (keine anderen)', 
    adminLevel: 'admin' 
  },

  // Donations
  'donation:manage': {
    label: 'Spendenempfänger verwalten',
    desc: 'Spendenempfänger pro Gedenkseite einladen und konfigurieren (Stripe Connect)',
    adminLevel: 'manager'
  },
  'donation:view:all': {
    label: 'Alle Spenden ansehen',
    desc: 'Systemweit alle Spendentransaktionen einsehen (Admin)',
    adminLevel: 'admin'
  },
};

// Helper: Typensicheres Array-Filtering für Permissions (string[] -> Permission[])
export function asPermissionArray(arr: unknown): Permission[] {
  if (!Array.isArray(arr)) return [];
  return arr.filter((v): v is Permission =>
    typeof v === "string" && Object.values(Permission).includes(v as Permission)
  );
}

// Für SDS Sammelrechnungserstellung berechtigte Permissions (zentrale Konstante)
export const SDS_INVOICE_API_PERMISSIONS: Permission[] = [
  Permission.InvoiceGenerateSDSCollective,
  Permission.SDSInvoice,
  Permission.SDSInvoiceCreate
];

export const DIRECTORY_AUDIENCE_CHOICES: Permission[] = [
  Permission.DirectoryAccess,
  // Falls du weitere, inhaltsspezifische Permissions für die Audience nutzen willst,
  // kannst du sie hier aufnehmen, z. B.:
  // Permission.MemorialCreate, Permission.BranchCreate, Permission.InvoiceGenerateSDSCollective, ...
];

 /* Permissions, die bestimmten Rollen explizit NICHT zugewiesen werden sollen
 * Auch wenn diese Rolle normalerweise "alle" Permissions hätte
 */
const ADMIN_EXCLUDED_PERMISSIONS: Permission[] = [
  'sds:admin',           // Nur für SDS-User, nicht für Hauptadmin
  'sds:export:exclusive' // Optional: Falls auch diese nur für SDS sein soll
];

// Role-based permission mapping - VOLLSTÄNDIG ERWEITERT mit allen DB-Table Permissions
const rolePermissions: Record<UserRole, Permission[]> = {
  admin: [
    // Admin hat alle Permissions AUSSER spezielle SDS-Admin Permissions
    // Diese sind explizit nur für SDS-User vorgesehen
    ...Object.values(Permission).filter(permission => 
      !ADMIN_EXCLUDED_PERMISSIONS.includes(permission)
    )
  ],
  
  subadmin: [
    // Fast alle Permissions außer kritischen Admin-Funktionen
      'admin:dashboard:access',
    // Admin Navigation Permissions für Subadmin
    'admin:nav:dashboard',
    'admin:nav:user_dashboard',
    'admin:nav:new_memorial',
    'admin:nav:all_memorials',
    'admin:nav:albums',
    'admin:nav:events',
    'admin:nav:family_trees',
    'admin:nav:collaborations',
    'admin:nav:memory_lights',
    'admin:nav:users',
    'admin:nav:permissions',
    'admin:nav:bestatter',
    'admin:nav:branches',
    'admin:nav:branches_structure',
    'admin:nav:embed_code',
    'admin:nav:sds_invoices',
    'admin:nav:sds_api_keys',
    'admin:nav:credits_packages',
    'admin:nav:credits_transfer',
    'admin:nav:credits_transactions',
    'admin:nav:quota_usage',
    'admin:nav:products',
    'admin:nav:bundles',
    'admin:nav:merchone',
    'admin:nav:merchone_catalog',
    'admin:nav:merchone_sync',
    'admin:nav:merchone_shop',
    'admin:nav:orders',
    'admin:nav:invoices',
    'admin:nav:plaques',
    'admin:nav:qrcodes',
    
    // Accounts (read-only)
    'accounts:view:all',
    // Activity Logs
    'activity_logs:view:all',
    // API Keys (view only)
    'api_keys:view:all', 'api_request_logs:view:all', 'apikey:view', 'apikey:view:all',
    // Bestatter Branches
    'bestatter_branches:view:all', 'bestatter_branches:create', 'bestatter_branches:edit',
    // Bulk Operations
    'bulk_operations:view:all', 'bulk_operations:create',
    // Cart Items
    'cart_items:view:all',
    // Collaboration
    'collaboration:invite', 'collaboration:manage', 'collaboration:remove', 'collaboration:view',
    'collaboration_invitations:view:all', 'collaboration_invitations:create', 'collaboration_invitations:edit',
    'collaborator_activity_logs:view:all',
    // Comments
    'comments:view:all', 'comments:create', 'comments:edit',
    // Credits
    'credits:view', 'credits:manage', 'credit_packages:view:all', 'credit_transactions:view:all',
    // Customer Entitlements
    'customer_entitlements:view:all', 'entitlements:view', 'entitlements:view:all', 'entitlements:manage',
    // Discount Codes
    'discount_codes:view:all', 'discount_codes:create', 'discount_codes:edit',
    'directory:access',
    'directory:provider:view', 'directory:provider:create', 'directory:provider:edit', 'directory:provider:delete',
    'directory:category:view', 'directory:category:create', 'directory:category:edit', 'directory:category:delete',
    'directory:item:view', 'directory:item:create', 'directory:item:edit', 'directory:item:delete',
    // Events
    'event:create', 'event:view', 'event:edit', 'event:delete', 'event:manage_rsvp',
    'event_comments:view:all', 'event_notifications:view:all', 'event_rsvp:view:all',
    // Family
    'family_tree:manage:all', 'family_tree:view', 'family_tree:edit', 'family_tree:delete',
    'family_tree:create', 'family_tree:export', 'family_tree:import',
    'family_link_requests:view:all', 'family_members:view:all', 'family_relationships:view:all',
    'family_section_settings:view:all',
    // Gallery
    'gallery:manage:all', 'gallery:view_private', 'gallery:create_album', 'gallery:delete_album',
    // Invoices
    'invoice:create', 'invoice:view', 'invoice:view:all', 'invoice:edit',
    'invoice:generate_sds_collective', 'invoice:generate_bestatter_collective',
    'invoice:generate_b2b', 'invoice:generate_pdf', 'invoice:send_email',
    'invoices:view:all', 'invoices:create', 'invoices:edit',
    // Media
    'media:upload', 'media:view', 'media:view:all', 'media:delete', 'media:manage',
    // Memberships
    'memberships:view:all', 'memberships:create', 'memberships:edit',
    // Memorial (all)
    'memorial:create', 'memorial:view', 'memorial:view:all', 'memorial:edit', 'memorial:edit:all',
    'memorial:delete:all', 'memorial:reassign', 'memorial:reassign:all',
    'memorial:extend:manage', 'memorial:view:expired', 'memorial:edit:time_restricted',
    'memorial:add_collaborator', 'memorial:remove_collaborator', 'memorial:manage_collaborators',
    // Memorial Albums
    'memorial_album_access:view:all', 'memorial_album_comments:view:all', 'memorial_album_downloads:view:all',
    'memorial_album_images:view:all', 'memorial_albums:view:all',
    // Memorial Collaborators
    'memorial_collaborators:view:all',
    // Memorial Events
    'memorial_events:view:all', 'memorial_events:create', 'memorial_events:edit',
    // Memorial Pages
    'memorial_page_album_access:view:all', 'memorial_page_album_items:view:all',
    'memorial_page_albums:view:all', 'memorial_page_videos:view:all', 'memorial_pages:view:all',
    // Memorial Visitor Uploads
    'memorial_visitor_uploads:view:all', 'memorial_visitor_uploads:moderate',
    // Memorial Groups
    'memorial_group:create', 'memorial_group:view', 'memorial_group:view:all', 'memorial_group:edit',
    'memorial_group:delete', 'memorial_group:add_member', 'memorial_group:remove_member', 'memorial_group:manage_permissions',
    // Memory Lights
    'memory_light:create', 'memory_light:create:own', 'memory_light:create:sds',
    'memory_light:approve', 'memory_light:moderate', 'memory_light:view_unapproved',
    'memory_light:delete', 'memory_light:view:all', 'memory_light:edit:all',
    'memory_lights:manage', 'memory_lights:moderate', 'memory_lights:view',
    // Moderation
    'moderation:view_queue', 'moderation:approve', 'moderation:reject', 'moderation:manage:all',
    // Orders
    'order:create', 'order:view', 'order:view:all', 'order:edit',
    // Plaque
    'plaque:create', 'plaque:view', 'plaque:edit', 'plaque:view:all',
    // Laser Orders
    'laser:order:create', 'laser:order:view', 'laser:order:edit',
    // Products
    'product:create', 'product:view', 'product:view:all', 'product:edit', 'product:delete',
    // Roles (view only)
    'roles:view:all',
    // Shipping Rates
    'shipping_rates:manage', 'shipping_rates:create', 'shipping_rates:edit',
    // Shop
    'shop:access', 'shop:cart:manage', 'shop:purchase', 'shop:checkout',
    // Subscriptions
    'subscriptions:view', 'subscriptions:create', 'subscriptions:edit',
    // Users
    'user:create', 'user:view', 'user:view:all', 'user:edit', 'user:check_email',
    // Branches
    'branch:view', 'branch:view:all', 'branch:create', 'branch:edit',
    // Other
    'embed:generate', 'view_reports',
    'email:plaque_status_send', 'email:plaque_order_admin_notification', 'email:password_send',
    'auth:login', 'auth:register', 'auth:login:google', 'auth:login:facebook',
    // Donations
    'donation:manage',
    // SDS
    'sds:user', 'sds:user:view', 'sds:user:edit',
    'sds:branch', 'sds:branch:view', 'sds:branch:edit',
    'sds:bestatter:view', 'sds:bestatter:edit', 'sds:bestatter:create',
    'sds:bestatter:read', 'sds:bestatter:update',
    'sds:invoice', 'sds:invoice:view', 'sds:invoice:edit',
    'sds:customer', 'sds:customer:view', 'sds:customer:edit',
    'sds:stats', 'sds:stats:view',
    'sds:document', 'sds:document:view', 'sds:document:create',
    'sds:case', 'sds:case:view', 'sds:case:edit',
    'sds:memorial', 'sds:memorial:view', 'sds:memorial:edit',
    'sds:bulk_invoices:view',
    'sds:memory_light:view', 'sds:memory_light:edit',
    // Donations
    'donation:manage', 'donation:view:all',
  ],
  
  bestatter: [
    // Bestatter-spezifische Permissions
    // Accounts (none)
    // Activity Logs (none)
    // API Keys (none)
    // Bestatter Branches
    'bestatter_branches:view:all', 'bestatter_branches:create', 'bestatter_branches:edit', 'bestatter_branches:delete',
    // Cart Items
    'cart_items:view:all', 'cart_items:create', 'cart_items:edit',
    // Collaboration
    'collaboration:invite', 'account:add_member', 'account:remove_member',
    'collaboration_invitations:create',
    // Customer Entitlements
    'customer_entitlements:view:all', 'customer_entitlements:create', 'customer_entitlements:edit', 'customer_entitlements:delete',
    'entitlements:create', 'entitlements:view', 'entitlements:view:own',
    'entitlements:edit', 'entitlements:delete', 'entitlements:manage',
    // Directory
    'directory:access',
    // Events
    'event:create', 'event:view', 'event:edit', 'event:delete',
    'event_rsvp:create', 'event_rsvp:view:all',
    // Family
    'family_tree:create', 'family_tree:view', 'family_tree:edit', 'family_tree:delete',
    'family_members:create', 'family_members:view:all', 'family_members:edit',
    // Gallery
    'gallery:create_album', 'gallery:manage_album', 'gallery:set_password',
    // Invoices
    'invoice:create', 'invoice:view', 'invoice:edit',
    'invoice:generate_bestatter_collective', 'invoice:generate_b2b',
    'invoice:generate_pdf', 'invoice:send_email',
    'invoices:view:all', 'invoices:create',
    // Media
    'media:upload', 'media:view', 'media:delete', 'media:manage',
    // Memorial
    'memorial:create', 'memorial:view', 'memorial:edit', 'memorial:reassign',
    'memorial:extend', 'memorial:add_collaborator', 'memorial:remove_collaborator',
    'memorial_pages:create', 'memorial_pages:view:all', 'memorial_pages:edit',
    'memorial_page_albums:create', 'memorial_page_albums:view:all',
    'memorial_visitor_uploads:create',
    // Memorial Groups
    'memorial_group:create', 'memorial_group:view', 'memorial_group:edit', 
    'memorial_group:add_member', 'memorial_group:remove_member',
    // Memory Lights
    'memory_light:create', 'memory_light:create:own',
    'memory_light:approve', 'memory_light:moderate',
    'memory_lights:create', 'memory_lights:view',
    // Orders
    'order:create', 'order:view',
    // Plaque
    'plaque:create', 'plaque:view', 'plaque:edit',
    // Laser Orders
    'laser:order:create', 'laser:order:view', 'laser:order:edit',
    // Shop
    'shop:access', 'shop:cart:manage', 'shop:purchase', 'shop:checkout',
    // Users & Branches
    'user:create', 'user:view', 'user:edit', 'user:delete', 'user:check_email',
    'branch:create', 'branch:view', 'branch:edit', 'branch:delete',
    // Other
    'embed:generate', 'credits:view',
    'email:password_send',
    'auth:login', 'auth:register', 'auth:login:google', 'auth:login:facebook',
  ],
  
  branch: [
    // Branch-spezifische Permissions (eingeschränkter als Bestatter)
    // Cart Items
    'cart_items:create', 'cart_items:edit',
    // Customer Entitlements
    'entitlements:view', 'entitlements:view:own',
    // Directory
    'directory:access',
    // Events
    'event:create', 'event:view', 'event:edit',
    'event_rsvp:create',
    // Family
    'family_tree:create', 'family_tree:view', 'family_tree:edit',
    'family_members:create',
    // Gallery
    'gallery:create_album', 'gallery:manage_album',
    // Invoices
    'invoice:view', 'invoice:generate_b2b', 'invoice:generate_pdf',
    // Media
    'media:upload', 'media:view', 'media:delete', 'media:manage',
    // Memorial
    'memorial:create', 'memorial:view', 'memorial:edit', 'memorial:reassign',
    'memorial:extend', 'memorial:add_collaborator',
    'memorial_pages:create', 'memorial_pages:edit',
    // Memorial Groups
    'memorial_group:view',
    // Memory Lights
    'memory_light:create', 'memory_light:create:own',
    'memory_lights:create',
    // Orders
    'order:create', 'order:view',
    // Plaque
    'plaque:create', 'plaque:view', 'plaque:edit',
    // Laser Orders
    'laser:order:create', 'laser:order:view', 'laser:order:edit',
    // Shop
    'shop:access', 'shop:cart:manage', 'shop:purchase', 'shop:checkout',
    // Other
    'credits:view',
    'auth:login', 'auth:register', 'auth:login:google', 'auth:login:facebook',
  ],
  
  user: [
    // Normale User Permissions (sehr eingeschränkt)
    // Cart Items
    'cart_items:create',
    // Comments
    'comments:create',
    // Directory
    'directory:access',
    // Events
    'event:create:custom', 'event:view', 'event:edit',
    'event_comments:create', 'event_rsvp:create',
    // Family
    'family_tree:create', 'family_tree:view', 'family_tree:edit',
    'family_members:create', 'family_link_requests:create',
    // Gallery
    'gallery:create_album', 'gallery:set_password', 'gallery:upload',
    // Invoices
    'invoice:view',
    'invoice:generate_pdf', 
    // Media
    'media:upload', 'media:view',
    // Memorial
    'memorial:create', 'memorial:view', 'memorial:edit',
    'memorial:extend',
    'memorial_visitor_uploads:create',
    'memorial_album_comments:create',
    // Memorial Groups
    'memorial_group:create', 'memorial_group:view', 'memorial_group:edit',
    // Memory Lights
    'memory_light:create', 'memory_light:create:own',
    'memory_light:upload_media',
    'memory_lights:create',
    // Orders
    'order:create', 'order:view',
    // Plaque
    'plaque:create', 'plaque:view',
    // Shop
    'shop:access', 'shop:cart:manage', 'shop:purchase', 'shop:checkout',
    // Users (Eigenes Profil)
    'user:edit',
    // Auth
    'auth:login', 'auth:register', 'auth:login:google', 'auth:login:facebook',
  ],
  
  sds_user: [
    // SDS User - erweiterte Verwaltungsrechte für alle Daten
    // Activity Logs
    'activity_logs:view:all',
    // API Keys
    'api_request_logs:view:all',
    // Bulk Operations
    'bulk_operations:view:all', 'bulk_operations:create',
    // Comments
    'comments:view:all',
    // Credits
    'credit:read', 'credits:view', 'credits:manage',
    'credit_packages:view:all', 'credit_transactions:view:all',
    // Customer Entitlements
    'customer_entitlements:view:all', 'customer_entitlements:create', 'customer_entitlements:edit', 'customer_entitlements:delete',
    'entitlements:create', 'entitlements:view', 'entitlements:view:own', 'entitlements:view:all',
    'entitlements:edit', 'entitlements:delete', 'entitlements:manage',
    // Events
    'event:view', 'event:edit', 'event:delete',
    'event_comments:view:all', 'event_notifications:view:all', 'event_rsvp:view:all',
    // Family
    'family_tree:view', 'family_tree:edit', 'family_tree:delete', 
    'family_tree:manage:all', 'family_tree:export',
    'family_members:view:all', 'family_relationships:view:all',
    // Gallery
    'gallery:view_private', 'gallery:manage:all',
    // Invoices
    'invoice:view', 'invoice:view:all', 'invoice:edit',
    'invoice:generate_sds_collective', 'invoice:generate_bestatter_collective',
    'invoice:generate_pdf', 'invoice:send_email',
    'invoices:view:all', 'invoices:edit',
    // Media
    'media:upload', 'media:view', 'media:view:all', 'media:delete', 'media:manage',
    // Memberships
    'memberships:view:all',
    // Memorial
    'memorial:view', 'memorial:view:all', 'memorial:edit:all',
    'memorial:extend:manage', 'memorial:view:expired',
    'memorial_pages:view:all', 'memorial_page_albums:view:all', 'memorial_page_videos:view:all',
    'memorial_visitor_uploads:view:all', 'memorial_visitor_uploads:moderate', 'memorial_visitor_uploads:delete',
    'memorial_albums:view:all', 'memorial_album_images:view:all',
    'memorial_collaborators:view:all', 'memorial_events:view:all',
    // Memorial Groups
    'memorial_group:view:all', 'memorial_group:manage_permissions',
    // Memory Lights
    'memory_light:create', 'memory_light:create:sds', 'memory_light:view_unapproved',
    'memory_light:approve', 'memory_light:moderate', 'memory_light:delete',
    'memory_light:view:all', 'memory_light:edit:all', 'memory_light:delete:all',
    'memory_lights:manage', 'memory_lights:moderate', 'memory_lights:delete', 'memory_lights:view',
    // Moderation
    'moderation:view_queue', 'moderation:approve', 'moderation:reject',
    // Orders
    'order:create', 'order:view', 'order:view:all',
    // Plaque
    'plaque:view', 'plaque:view:all',
    // Laser Orders
    'laser:order:create', 'laser:order:view', 'laser:order:edit',
    // Shop
    'shop:access', 'shop:cart:manage', 'shop:purchase', 'shop:checkout',
    // Users & Branches
    'user:check_email', 'user:view', 'user:view:all',
    'branch:view', 'branch:view:all',
    // Other
    'email:password_send',
    'auth:login', 'auth:register', 'auth:login:google', 'auth:login:facebook',
    'ai:memorial-suggestion',
    // SDS specific
    'sds:user', 'sds:user:view', 'sds:user:edit', 'sds:user:delete', 'sds:user:create',
    'sds:branch', 'sds:branch:view', 'sds:branch:edit', 'sds:branch:delete', 'sds:branch:create',
    'sds:bestatter:view', 'sds:bestatter:edit', 'sds:bestatter:delete', 'sds:bestatter:create',
    'sds:bestatter:read', 'sds:bestatter:update',
    'sds:invoice', 'sds:invoice:view', 'sds:invoice:edit', 'sds:invoice:create',
    'sds:customer', 'sds:customer:view', 'sds:customer:edit', 'sds:customer:create', 'sds:customer:delete',
    'sds:stats', 'sds:stats:view',
    'sds:document', 'sds:document:view', 'sds:document:create', 'sds:document:edit', 'sds:document:delete',
    'sds:case', 'sds:case:view', 'sds:case:edit', 'sds:case:create', 'sds:case:delete',
    'sds:memorial', 'sds:memorial:view', 'sds:memorial:edit', 'sds:memorial:create', 'sds:memorial:delete',
    'sds:bulk_invoices:view',
    'sds:memory_light:view', 'sds:memory_light:create', 'sds:memory_light:edit', 'sds:memory_light:delete',
    'sds:event:view', 'sds:event:create', 'sds:event:edit', 'sds:event:delete',
    'sds:gallery:view', 'sds:gallery:create_album', 'sds:gallery:manage_album', 'sds:gallery:delete_album',
    'sds:gallery:upload', 'sds:gallery:download', 'sds:gallery:set_password',
    'sds:family_tree:view', 'sds:family_tree:create', 'sds:family_tree:edit', 'sds:family_tree:delete',
    'sds:family_tree:export', 'sds:family_tree:import',
    'sds:collaboration:view', 'sds:collaboration:invite', 'sds:collaboration:manage', 'sds:collaboration:remove',
    'sds:employee:view', 'sds:employee:create', 'sds:employee:edit', 'sds:employee:delete',
    'sds:admin',  // NEU: Markiert SDS-Admin
    'sds:export:exclusive',  // NEU: Nur SDS-Exporte erlaubt
    // Donations
    'donation:manage', 'donation:view:all',
  ],

  // ── Kreuzmal ── Laser-Hersteller / Versandpartner ────────────────────────────
  kreuzmal: [
    'auth:login',
    'laser:order:view',
    'laser:order:ship',
  ],
};

// ========== DATABASE PERMISSION SUPPORT (rest of the file remains the same) ==========

/**
 * Cache für DB-Permissions (nur Server-seitig)
 * Wird beim App-Start gefüllt und periodisch aktualisiert
 */
let dbPermissionsCache: Record<UserRole, Permission[]> | null = null;
let cacheTimestamp: number = 0;
const CACHE_TTL = 5 * 60 * 1000; // 5 Minuten Cache

/**
 * Lädt Permissions aus der Datenbank (nur Server-seitig)
 * Diese Funktion sollte nur in Server-Komponenten verwendet werden
 */
export async function loadDbPermissions(): Promise<Record<UserRole, Permission[]> | null> {
  // Prüfe ob wir im Browser sind
  if (typeof window !== 'undefined') {
    return null; // Client-seitig nutzen wir nur Code-Defaults
  }
  
  // Cache prüfen
  if (dbPermissionsCache && Date.now() - cacheTimestamp < CACHE_TTL) {
    return dbPermissionsCache;
  }
  
  try {
    // Dynamischer Import nur Server-seitig
    const { createClient } = await import('@supabase/supabase-js');
    
    if (!process.env.NEXT_PUBLIC_SUPABASE_URL || !process.env.SUPABASE_SERVICE_ROLE_KEY) {
      console.warn('Supabase credentials not found, using code defaults');
      return null;
    }
    
    const supabase = createClient(
      process.env.NEXT_PUBLIC_SUPABASE_URL,
      process.env.SUPABASE_SERVICE_ROLE_KEY,
      {
        auth: {
          autoRefreshToken: false,
          persistSession: false
        }
      }
    );
    
    // Lade System-Rollen aus DB
    const { data: roles, error } = await supabase
      .from('roles')
      .select('key, permissions')
      .eq('is_system', true)
      .not('permissions', 'is', null);
    
    if (error) {
      console.error('Error loading DB permissions:', error);
      return null;
    }
    
    if (!roles || roles.length === 0) {
      return null;
    }
    
    // Konvertiere zu Record Format
    const dbPerms: Record<string, Permission[]> = {};
    
    roles.forEach(role => {
      if (role.key && Array.isArray(role.permissions)) {
        // Validiere dass alle Permissions gültig sind
        const validPerms = role.permissions.filter(p => 
          Object.values(Permission).includes(p as Permission)
        ) as Permission[];
        
        if (validPerms.length > 0) {
          dbPerms[role.key as UserRole] = validPerms;
        }
      }
    });
    
    // Cache aktualisieren
    dbPermissionsCache = dbPerms as Record<UserRole, Permission[]>;
    cacheTimestamp = Date.now();
    
    console.log(`✅ Loaded permissions from DB for roles: ${Object.keys(dbPerms).join(', ')}`);
    return dbPermissionsCache;
    
  } catch (error) {
    console.error('Failed to load DB permissions:', error);
    return null;
  }
}

/**
 * Clear the permissions cache (z.B. nach Updates)
 */
export function clearPermissionsCache() {
  dbPermissionsCache = null;
  cacheTimestamp = 0;
}

/**
 * Check if a user role has a specific permission
 * Prüft zuerst DB-Permissions (falls geladen), dann Code-Defaults
 */
export function hasPermission(role: UserRole, permission: Permission): boolean {
  // Prüfe zuerst DB-Cache (nur Server-seitig)
  if (dbPermissionsCache && dbPermissionsCache[role]) {
    return dbPermissionsCache[role].includes(permission);
  }
  
  // Fallback auf Code-Defaults
  return rolePermissions[role]?.includes(permission) || false;
}

/**
 * Get all permissions for a specific role
 * Nutzt DB-Permissions falls vorhanden, sonst Code-Defaults
 */
export function getPermissionsForRole(role: UserRole): Permission[] {
  // Prüfe zuerst DB-Cache (nur Server-seitig)
  if (dbPermissionsCache && dbPermissionsCache[role]) {
    return [...dbPermissionsCache[role]];
  }
  
  // Fallback auf Code-Defaults
  return [...(rolePermissions[role] || [])];
}

/**
 * Asynchrone Version für Server-Komponenten
 * Lädt Permissions aus DB falls nötig
 */
export async function getPermissionsForRoleAsync(role: UserRole): Promise<Permission[]> {
  // Versuche DB-Permissions zu laden (nur Server-seitig)
  const dbPerms = await loadDbPermissions();
  
  if (dbPerms && dbPerms[role]) {
    return [...dbPerms[role]];
  }
  
  // Fallback auf Code-Defaults
  return [...(rolePermissions[role] || [])];
}

/**
 * Helper für Server-Side Rendering
 * Sollte in Server-Komponenten beim Start aufgerufen werden
 */
export async function initializePermissions() {
  if (typeof window === 'undefined') {
    // Nur Server-seitig
    await loadDbPermissions();
  }
}

// ========== END DATABASE SUPPORT ==========

/**
 * Check if memorial is editable based on time restrictions
 */
export function isMemorialEditable(editableUntil: Date | string | null): boolean {
  if (!editableUntil) return true; // No restriction set
  const until = typeof editableUntil === 'string' ? new Date(editableUntil) : editableUntil;
  return until > new Date();
}

/**
 * Get remaining edit days for a memorial
 */
export function getRemainingEditDays(editableUntil: Date | string | null): number {
  if (!editableUntil) return -1; // No restriction
  const until = typeof editableUntil === 'string' ? new Date(editableUntil) : editableUntil;
  const now = new Date();
  if (until <= now) return 0;
  
  const diffTime = Math.abs(until.getTime() - now.getTime());
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return diffDays;
}

// --------- MEMBERSHIP PERMISSION HELPER (for Context and Hooks) ---------
/**
 * Checks if a given membership (object with role_id and permissions) has a specific permission.
 * This allows for more flexible per-user/membership permission extension.
 */
export function hasMembershipPermission(
  membership: { role_id: UserRole; permissions?: Permission[] } | null | undefined,
  permission: Permission
): boolean {
  if (!membership) return false;
  // Prefer explicit membership.permissions if present, otherwise by role
  if (Array.isArray(membership.permissions) && membership.permissions.length > 0) {
    return membership.permissions.includes(permission);
  }
  return hasPermission(membership.role_id, permission);
}

/**
 * Prüft, ob der Nutzer mindestens eine der angegebenen Permissions besitzt.
 */
export function hasAnyMembershipPermission(
  membership: { role_id: UserRole; permissions?: Permission[] } | null | undefined,
  permissions: Permission[]
): boolean {
  if (!membership) return false;
  for (const perm of permissions) {
    if (hasMembershipPermission(membership, perm)) {
      return true;
    }
  }
  return false;
}

/**
 * Check if user has access to a specific branch
 */
export function hasAccessToBranch(
  userRole: UserRole,
  userId: string,
  bestatterId: string | null,
  branch: { id: string; bestatter_id: string }
): boolean {
  if (userRole === 'admin' || userRole === 'subadmin') return true;
  if (userRole === 'branch') return userId === branch.id;
  if (userRole === 'bestatter' && bestatterId) return branch.bestatter_id === bestatterId;
  if (userRole === 'sds_user') return true;
  return false;
}

/**
 * Check if user has access to manage a specific user
 */
export function hasAccessToUser(
  userRole: UserRole,
  bestatterId: string | null,
  user: { branch_id: string; branch_bestatter_id: string }
): boolean {
  if (userRole === 'admin' || userRole === 'subadmin') return true;
  if (userRole === 'bestatter' && bestatterId) return user.branch_bestatter_id === bestatterId;
  if (userRole === 'sds_user') return true;
  return false;
}

/**
 * Check if user has access to a memorial page
 */
export function hasAccessToMemorial(
  userRole: UserRole,
  userId: string,
  bestatterId: string | null,
  memorialOwnerId: string
): boolean {
  if (userRole === 'admin' || userRole === 'subadmin') return true;
  if (userRole === 'bestatter') return true;
  if (userRole === 'branch') return true;
  if (userRole === 'sds_user') return true;
  return userId === memorialOwnerId;
}

/**
 * Check if user has shop access
 */
export function hasShopAccess(userRole: UserRole): boolean {
  return hasPermission(userRole, 'shop:access');
}

/**
 * Check if user can generate SDS collective invoices
 */
export function canGenerateSDSInvoices(userRole: UserRole): boolean {
  return hasPermission(userRole, 'invoice:generate_sds_collective');
}

/**
 * SDS Permissions for API Keys - Erweitert mit allen neuen Permissions
 */
export const SDS_PERMISSIONS = [
  // Legacy (für Rückwärtskompatibilität)
  { value: "sds:invoice", label: "Rechnungen verwalten (Legacy)", desc: "Vollzugriff auf Rechnungen (alte Version)" },
  { value: "sds:customer", label: "Kunden verwalten (Legacy)", desc: "Vollzugriff auf Kunden (alte Version)" },
  { value: "sds:stats", label: "Statistiken abrufen (Legacy)", desc: "Vollzugriff auf Statistiken (alte Version)" },
  { value: "sds:document", label: "Dokumente verwalten (Legacy)", desc: "Vollzugriff auf Dokumente (alte Version)" },
  { value: "sds:case", label: "Sterbefälle verwalten (Legacy)", desc: "Vollzugriff auf Sterbefälle (alte Version)" },
  { value: "sds:user", label: "Benutzer verwalten (Legacy)", desc: "Vollzugriff auf Benutzer (alte Version)" },
  { value: "sds:branch", label: "Filialen verwalten (Legacy)", desc: "Vollzugriff auf Filialen (alte Version)" },
  { value: "sds:memorial", label: "Gedenkseiten verwalten (Legacy)", desc: "Vollzugriff auf Gedenkseiten (alte Version)" },
  // Invoice (Granular)
  { value: "sds:invoice:view", label: "SDS-Rechnungen ansehen", desc: "Alle SDS Rechnungen anzeigen" },
  { value: "sds:invoice:edit", label: "SDS-Rechnungen bearbeiten", desc: "SDS Rechnungen ändern" },
  { value: "sds:invoice:create", label: "SDS-Rechnungen erstellen", desc: "Neue SDS Rechnungen anlegen" },
  // Customer (Granular)
  { value: "sds:customer:view", label: "SDS-Kunden ansehen", desc: "SDS Kunden einsehen" },
  { value: "sds:customer:edit", label: "SDS-Kunden bearbeiten", desc: "SDS Kunden ändern" },
  { value: "sds:customer:create", label: "SDS-Kunden erstellen", desc: "Neue SDS Kunden anlegen" },
  { value: "sds:customer:delete", label: "SDS-Kunden löschen", desc: "SDS Kunden entfernen" },
  // Stats (Granular)
  { value: "sds:stats:view", label: "SDS-Statistiken ansehen", desc: "Statistiken und Auswertungen" },
  // Document (Granular)
  { value: "sds:document:view", label: "SDS-Dokumente ansehen", desc: "SDS Dokumente einsehen" },
  { value: "sds:document:create", label: "SDS-Dokumente hochladen", desc: "Neue SDS Dokumente hinzufügen" },
  { value: "sds:document:edit", label: "SDS-Dokumente bearbeiten", desc: "SDS Dokumente ändern" },
  { value: "sds:document:delete", label: "SDS-Dokumente löschen", desc: "SDS Dokumente entfernen" },
  // Case (Granular)
  { value: "sds:case:view", label: "Sterbefälle ansehen", desc: "SDS Sterbefälle einsehen" },
  { value: "sds:case:edit", label: "Sterbefälle bearbeiten", desc: "Sterbefälle ändern" },
  { value: "sds:case:create", label: "Sterbefälle anlegen", desc: "Neue Sterbefälle anlegen" },
  { value: "sds:case:delete", label: "Sterbefälle löschen", desc: "Sterbefälle entfernen" },
  // User (Granular)
  { value: "sds:user:view", label: "SDS-Benutzer ansehen", desc: "SDS Benutzer einsehen" },
  { value: "sds:user:edit", label: "SDS-Benutzer bearbeiten", desc: "SDS Benutzer ändern" },
  { value: "sds:user:create", label: "SDS-Benutzer anlegen", desc: "SDS Benutzer neu anlegen" },
  { value: "sds:user:delete", label: "SDS-Benutzer löschen", desc: "SDS Benutzer entfernen" },
  // Branch (Granular)
  { value: "sds:branch:view", label: "SDS-Filialen ansehen", desc: "SDS Filialen einsehen" },
  { value: "sds:branch:edit", label: "SDS-Filialen bearbeiten", desc: "SDS Filialen ändern" },
  { value: "sds:branch:create", label: "SDS-Filialen anlegen", desc: "Neue SDS Filialen anlegen" },
  { value: "sds:branch:delete", label: "SDS-Filialen löschen", desc: "SDS Filialen entfernen" },
  // Memorial (Granular)
  { value: "sds:memorial:view", label: "SDS-Gedenkseiten ansehen", desc: "SDS Gedenkseiten einsehen" },
  { value: "sds:memorial:edit", label: "SDS-Gedenkseiten bearbeiten", desc: "SDS Gedenkseiten ändern" },
  { value: "sds:memorial:create", label: "SDS-Gedenkseiten anlegen", desc: "Neue SDS Gedenkseiten anlegen" },
  { value: "sds:memorial:delete", label: "SDS-Gedenkseiten löschen", desc: "SDS Gedenkseiten entfernen" },
  // Memory Light (Granular)
  { value: "sds:memory_light:view", label: "SDS-Gedenklichter ansehen", desc: "SDS Memory Lights einsehen" },
  { value: "sds:memory_light:create", label: "SDS-Gedenklichter anlegen", desc: "Neue SDS Memory Lights" },
  { value: "sds:memory_light:edit", label: "SDS-Gedenklichter bearbeiten", desc: "SDS Memory Lights ändern" },
  { value: "sds:memory_light:delete", label: "SDS-Gedenklichter löschen", desc: "SDS Memory Lights entfernen" },
  // Bestatter (Granular)
  { value: "sds:bestatter:view", label: "SDS-Bestatter ansehen", desc: "Bestatterdaten einsehen" },
  { value: "sds:bestatter:edit", label: "SDS-Bestatter bearbeiten", desc: "Bestatterdaten bearbeiten" },
  { value: "sds:bestatter:create", label: "SDS-Bestatter anlegen", desc: "Neuen Bestatter anlegen" },
  { value: "sds:bestatter:delete", label: "SDS-Bestatter löschen", desc: "Bestatter löschen" },
  { value: "sds:bestatter:read", label: "SDS-Bestatter lesen (Legacy)", desc: "Bestatterdaten lesen (alte Version)" },
  { value: "sds:bestatter:update", label: "SDS-Bestatter aktualisieren (Legacy)", desc: "Bestatterdaten ändern (alte Version)" },
  // Events (Granular)
  { value: "sds:event:view", label: "SDS-Events ansehen", desc: "SDS Events einsehen" },
  { value: "sds:event:create", label: "SDS-Events anlegen", desc: "Neue SDS Events erstellen" },
  { value: "sds:event:edit", label: "SDS-Events bearbeiten", desc: "SDS Events ändern" },
  { value: "sds:event:delete", label: "SDS-Events löschen", desc: "SDS Events entfernen" },
  // Family Tree (Granular)
  { value: "sds:family_tree:view", label: "SDS-Stammbäume ansehen", desc: "SDS Stammbäume einsehen" },
  { value: "sds:family_tree:create", label: "SDS-Stammbäume anlegen", desc: "Neue SDS Stammbäume erstellen" },
  { value: "sds:family_tree:edit", label: "SDS-Stammbäume bearbeiten", desc: "SDS Stammbäume ändern" },
  { value: "sds:family_tree:delete", label: "SDS-Stammbäume löschen", desc: "SDS Stammbäume entfernen" },
  { value: "sds:family_tree:export", label: "SDS-Stammbäume exportieren", desc: "SDS Stammbäume als Datei exportieren" },
  { value: "sds:family_tree:import", label: "SDS-Stammbäume importieren", desc: "SDS Stammbäume aus Datei importieren" },
  // Gallery (Granular)
  { value: "sds:gallery:view", label: "SDS-Galerien ansehen", desc: "SDS Galerien einsehen" },
  { value: "sds:gallery:create_album", label: "SDS-Alben erstellen", desc: "Neue SDS Alben anlegen" },
  { value: "sds:gallery:manage_album", label: "SDS-Alben verwalten", desc: "SDS Alben verwalten" },
  { value: "sds:gallery:delete_album", label: "SDS-Alben löschen", desc: "SDS Alben entfernen" },
  { value: "sds:gallery:upload", label: "SDS-Bilder hochladen", desc: "Bilder in SDS Galerien hochladen" },
  { value: "sds:gallery:download", label: "SDS-Bilder herunterladen", desc: "Bilder aus SDS Galerien herunterladen" },
  { value: "sds:gallery:set_password", label: "SDS-Album-Passwörter setzen", desc: "Passwörter für SDS Alben vergeben" },
  // Collaboration (Granular)
  { value: "sds:collaboration:view", label: "SDS-Kollaborationen ansehen", desc: "SDS Kollaborationen einsehen" },
  { value: "sds:collaboration:invite", label: "SDS-Kollaboratoren einladen", desc: "Neue SDS Kollaboratoren einladen" },
  { value: "sds:collaboration:manage", label: "SDS-Kollaborationen verwalten", desc: "SDS Kollaborationen verwalten" },
  { value: "sds:collaboration:remove", label: "SDS-Kollaboratoren entfernen", desc: "SDS Kollaboratoren entfernen" },
  // Employee (Granular)
  { value: "sds:employee:view", label: "SDS-Mitarbeiter ansehen", desc: "SDS Mitarbeiter einsehen" },
  { value: "sds:employee:create", label: "SDS-Mitarbeiter anlegen", desc: "Neue SDS Mitarbeiter erstellen" },
  { value: "sds:employee:edit", label: "SDS-Mitarbeiter bearbeiten", desc: "SDS Mitarbeiter ändern" },
  { value: "sds:employee:delete", label: "SDS-Mitarbeiter löschen", desc: "SDS Mitarbeiter entfernen" },
  // Bulk Invoices
  { value: "sds:bulk_invoices:view", label: "Sammelrechnungen ansehen", desc: "Bulk-Invoices abrufen" },
  // AI
  { value: "ai:memorial-suggestion", label: "AI Nachruf Vorschlag", desc: "AI-gestützte Nachruf-Vorschläge" },
  // Credits
  { value: "credit:read", label: "Creditpakete lesen", desc: "Creditpakete abrufen" },
  { value: "credit:purchase", label: "Creditpakete kaufen", desc: "Creditpakete bestellen" },
];
